/* eslint-disable no-unreachable */
import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from "jwt-decode";
import TokenManager from './tokenManager';
import { diagnosticEndpoint, safeApiDiagnostic, safeErrorDiagnostic } from './errorDiagnostics';
import {
  cleanRequestData,
  normalizeComponentFormRequest,
  validateRequestData,
} from '../views/admin/management/pagemanagement/utils/requestBuilder';
import { getLocalizedText, formatUniqueNameForDisplay } from '../utils/localization';
import { getActiveClientIdForApi } from '../utils/activeClientId';
import { isLocalDevelopmentHost, normalizeCustomAppHostname } from '../utils/domainUtils';
import { resolvePublicSignInSso, SIGN_IN_BOOTSTRAP_TIMEOUT_MS } from './signInBootstrap';

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean; _skipAuthRedirect?: boolean };

/** Opcional: sólo con REACT_APP_BUBLOUD_DEVICE_MODE_V2 (ver docs). */
export type BubloudSessionStartPayload = {
  transport?: 'webrtc';
  transportPreference?: 'AUTO' | 'FORCE_WEBRTC';
  deviceProfile?: {
    modeCandidate?: string;
    raw?: Record<string, unknown>;
    timestamp?: number;
  };
  manualModeOverride?: 'auto' | 'mobile' | 'tablet' | 'workstation';
  clientViewport?: { width: number; height: number };
};

type BubloudSessionRequestPayload = {
  bubbleId: string;
  keyScope?: 'personal' | 'company';
  vmId?: string;
  resolution?: { width: number; height: number };
  metadata?: Record<string, any>;
  protocol?: string;
  /** No rompe backends antiguos: campos ignorados si la lambda no tiene flag. */
  sessionStart?: BubloudSessionStartPayload;
};

type BubloudSessionResponse = {
  sessionId?: string;
  tunnelUrl?: string;
  token?: string;
  expiresAt?: string;
  diagnostics?: Record<string, any>;
  [key: string]: any;
};

type BubloudTrialSessionResponse = BubloudSessionResponse & {
  reservationId?: string;
  entitlementId?: string;
  bubbleId?: string;
  vmId?: string;
  poolKey?: string;
};

type AnalyticsCaptureRequestPayload = {
  event: string;
  distinct_id?: string;
  properties?: Record<string, unknown>;
  timestamp?: string | number;
};

const FRONTEND_API_ERROR_EVENT = 'orgcloud:api-error';

const emitFrontendApiError = (payload: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(FRONTEND_API_ERROR_EVENT, { detail: payload }));
  } catch {
    // ignore analytics dispatch issues
  }
};

const normalizeAnalyticsResponse = (raw: any) => {
  if (Array.isArray(raw?.items)) return raw;

  const payload = raw?.data;
  const columns = Array.isArray(payload?.columns) ? payload.columns : [];
  const results = Array.isArray(payload?.results) ? payload.results : [];

  if (!columns.length || !results.length) {
    return {
      ...raw,
      items: [],
    };
  }

  const items = results.map((row: any) => {
    if (Array.isArray(row)) {
      return Object.fromEntries(columns.map((column: string, index: number) => [column, row[index]]));
    }
    if (row && typeof row === 'object') {
      return row;
    }
    return { value: row };
  });

  return {
    ...raw,
    items,
  };
};

const parseAnalyticsPeriodToDays = (value: unknown): number | undefined => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return undefined;
  const aliases: Record<string, number> = {
    '24h': 1,
    '1d': 1,
    '7d': 7,
    '14d': 14,
    '30d': 30,
    '90d': 90,
  };
  if (aliases[raw]) return aliases[raw];
  if (/^\d+d$/.test(raw)) return Math.max(1, Math.min(Number(raw.slice(0, -1)), 90));
  if (/^\d+w$/.test(raw)) return Math.max(1, Math.min(Number(raw.slice(0, -1)) * 7, 90));
  return undefined;
};

type BubloudSessionConnectResponse = {
  webrtc_token?: string;
  file_tunnel_token?: string;
  clipboard_token?: string;
  expires_in?: number;
  vm_id?: string;
  vm_endpoint?: string;
  session_id?: string;
  webrtcSignalingUrl?: string;
  mediasoupSignalingUrl?: string;
  preferredTransport?: 'webrtc';
  sessionLifecycle?: {
    idleFreezeMs?: number;
    backgroundThrottle?: boolean;
    resumeTargetMs?: number;
    reconnectWithoutReset?: boolean;
  };
  mode?: 'mobile' | 'tablet' | 'workstation';
  profile?: string;
  [key: string]: any;
};

type DesktopCapabilitiesPayload = {
  sessionId?: string;
  bubbleId?: string;
  vmId?: string;
  profile?: string;
  capabilities: {
    viewportW: number;
    viewportH: number;
    dpr: number;
    pointerFine: boolean;
    pointerCoarse: boolean;
    hover: boolean;
    mode: 'mobile' | 'tablet' | 'workstation';
    inputs: Array<'touch' | 'mouse' | 'keyboard'>;
    keyboardUsage?: boolean;
    visualViewportW?: number;
    visualViewportH?: number;
    visualViewportScale?: number;
    externalDisplayLikely?: boolean;
    maxTouchPoints?: number;
    hasVirtualKeyboardApi?: boolean;
    hasRemotePlaybackApi?: boolean;
  };
};

export type CompanySsoPublicConfig = {
  Enabled: boolean;
  ProviderType: 'OIDC' | 'SAML';
  ProviderName: string;
  DisplayName: string;
  LoginMode: 'disabled' | 'optional' | 'enforced';
  AutoJoinDomains: string[];
  JitProvisioning: boolean;
  AllowPasswordFallback: boolean;
  AttributeMapping?: Record<string, string>;
  RoleMapping?: Record<string, string[]>;
  Cognito?: {
    ProviderName?: string;
    Status?: 'draft' | 'active' | 'disabled' | 'error';
    LastSyncedAt?: string;
  };
};

export type PublicClientIdentity = {
  EntryId?: string;
  UniqueName?: string;
  Information?: {
    CustomAppHostname?: string;
    SSO?: CompanySsoPublicConfig;
    General?: Record<string, any>;
  };
};

// URLs que no deben intentar refresh de token (endpoints públicos)
const PUBLIC_ENDPOINTS = [
  'signup',
  'accessPublicForm',
  'accessClient',
  'accessFav',
  'accessLandingPage',
  'accessLocalAuth',
  'public/reservationAvailability',
  'public/reservationHold',
  'public/reservationConfirm',
];
const ANALYTICS_ENDPOINTS = ['analytics/capture-proxy', 'analytics/public-capture'];

axios.interceptors.response.use(
  (response) => {
    // Verificar si la respuesta es HTML en lugar de JSON
    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('text/html')) {
      console.error('[ApiService] Respuesta HTML recibida en lugar de JSON:', diagnosticEndpoint(response.config.url));
      const error = new Error('El servidor devolvió HTML en lugar de JSON. Verifica la configuración del API Gateway.');
      (error as any).response = response;
      (error as any).isHtmlResponse = true;
      return Promise.reject(error);
    }
    return response;
  },
  async (error: AxiosError) => {
    // Verificar si la respuesta de error es HTML
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'string' && responseData.trim().startsWith('<!DOCTYPE')) {
      console.error('[ApiService] Error HTML recibido:', diagnosticEndpoint(error.config?.url));
      const htmlError = new Error('El servidor devolvió HTML en lugar de JSON. Esto puede indicar un problema de routing o configuración del API Gateway.');
      (htmlError as any).response = error.response;
      (htmlError as any).isHtmlResponse = true;
      (htmlError as any).originalError = error;
      return Promise.reject(htmlError);
    }

    const status = error.response?.status;
    const message = TokenManager.extractErrorMessage(error);
    const originalRequest = error.config as RetriableRequestConfig | undefined;
    const requestUrl = originalRequest?.url || '';
    const requestMethod = String(originalRequest?.method || 'get').toUpperCase();
    const isAnalyticsEndpoint = ANALYTICS_ENDPOINTS.some((endpoint) => requestUrl.includes(endpoint));
    const emitCurrentError = () =>
      !isAnalyticsEndpoint &&
      emitFrontendApiError(safeApiDiagnostic(requestUrl, requestMethod, error));
    
    // No intentar refresh para endpoints públicos
    const isPublicEndpoint = PUBLIC_ENDPOINTS.some(endpoint => requestUrl.includes(endpoint));
    
    if (isPublicEndpoint) {
      emitCurrentError();
      return Promise.reject(error);
    }

    // 401 sin cuerpo (API Gateway/Lambda) no trae "token" en el mensaje; igual debemos refrescar si hay refresh.
    const isAuthError =
      !!originalRequest &&
      !originalRequest?._retry &&
      !!status &&
      (status === 401 ||
        ((status === 400 || status === 403) && TokenManager.isTokenError(message)));

    if (!isAuthError) {
      emitCurrentError();
      return Promise.reject(error);
    }

    if (!originalRequest) {
      emitCurrentError();
      return Promise.reject(error);
    }

    const sentAuthorization = AxiosHeaders.from(originalRequest.headers ?? new AxiosHeaders()).get('Authorization');
    const sentToken = typeof sentAuthorization === 'string' ? /^Bearer\s+(.+)$/i.exec(sentAuthorization)?.[1] || '' : '';
    const isOriginalSessionCurrent = TokenManager.captureRequestSession(sentToken);
    if (!isOriginalSessionCurrent()) return Promise.reject(TokenManager.sessionChangedError());

    if (!TokenManager.shouldAttemptRefresh()) {
      emitCurrentError();
      if (!(originalRequest as RetriableRequestConfig)?._skipAuthRedirect) {
        TokenManager.clearSession();
      }
      // No redirigir si la petición fue marcada para skip (ej. flujo Bubloud en modal)
      // para evitar redirect abrupto cuando el usuario está conectando; el componente puede mostrar error.
      if (!(originalRequest as RetriableRequestConfig)?._skipAuthRedirect) {
        TokenManager.redirectToLogin();
      }
      return Promise.reject(error);
    }

    try {
      // TokenManager shares renewals only while their original session is current.
      const newToken = sentToken === TokenManager.getIdToken()
        ? await TokenManager.refreshSession() : TokenManager.getIdToken();
      if (!isOriginalSessionCurrent() || TokenManager.getIdToken() !== newToken) throw TokenManager.sessionChangedError();
      const headers = AxiosHeaders.from(originalRequest.headers ?? new AxiosHeaders());
      headers.set('Authorization', `Bearer ${newToken}`);
      headers.set('authorization', `Bearer ${newToken}`);
      originalRequest.headers = headers;
      originalRequest._retry = true;
      return axios.request(originalRequest);
    } catch (refreshError) {
      // A late refresh from a previous identity must not sign out the new one.
      if (TokenManager.isSessionChangedError(refreshError) || !isOriginalSessionCurrent()) return Promise.reject(refreshError);
      emitCurrentError();
      if (!(originalRequest as RetriableRequestConfig)?._skipAuthRedirect) {
        TokenManager.clearSession();
      }
      if (!(originalRequest as RetriableRequestConfig)?._skipAuthRedirect) {
        TokenManager.redirectToLogin();
      }
      return Promise.reject(refreshError);
    }
  }
);

class ApiService {
  private static publicBrandingRequestCache = new Map<string, Promise<any>>();
  private static publicLandingRequestCache = new Map<string, Promise<LandingPageConfig>>();
  private static publicClientRequestCache = new Map<string, Promise<PublicClientIdentity>>();
  private static userDataRequestCache = new Map<string, { expiresAt: number; request: Promise<any> }>();
  private static objectSchemasRequestCache = new Map<string, { expiresAt: number; request: Promise<any> }>();
  private static pagesRequestCache = new Map<string, { expiresAt: number; request: Promise<any> }>();
  private static queryDataInFlight = new Map<string, Promise<any>>();
  private static rowsInFlight = new Map<string, Promise<any>>();
  private static reservationsReadRequestCache = new Map<string, { expiresAt: number; request: Promise<any> }>();
  private static readonly SHORT_TTL_MS = 15000;

  private static apiGatewayBaseUrl: string = (() => {
    const normalizeBase = (base: string): string => (base.endsWith('/') ? base : `${base}/`);
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalhost = !!host && isLocalDevelopmentHost(host);
    // localhost, IPs privadas y hosts *.ts.net se sirven junto al gateway local.
    // Un build optimizado sigue siendo local: enviarlo a API Gateway rompería
    // los JWT demo emitidos por accessLocalAuth y expulsaría la sesión.
    const useLocalProxy = isLocalhost;
    if (useLocalProxy) {
      return '/V1/';
    }

    const envBase = (process.env.REACT_APP_API_GATEWAY_BASE_URL || '').trim();
    if (envBase) {
      return normalizeBase(envBase);
    }

    return 'https://a9obsrl0ph.execute-api.eu-west-3.amazonaws.com/V1/';
  })()

  private static getLocalAuthBaseUrl(): string {
    const normalizeBase = (base: string): string => (base.endsWith('/') ? base : `${base}/`);
    if (typeof window !== 'undefined' && isLocalDevelopmentHost(window.location.hostname)) {
      return '/V1/';
    }
    return normalizeBase(ApiService.apiGatewayBaseUrl);
  }

  private static buildPublicClientQuery(identifier: string) {
    const normalizedIdentifier = normalizeCustomAppHostname(identifier) || String(identifier || '').trim().toLowerCase();
    if (!normalizedIdentifier) {
      throw new Error('Tenant identifier is required');
    }
    if (normalizedIdentifier.includes('.')) {
      return `host=${encodeURIComponent(normalizedIdentifier)}`;
    }
    return `urlSubdomain=${encodeURIComponent(normalizedIdentifier)}`;
  }

  private static getShortJwtKey(jwt: string | undefined) {
    if (!jwt) return 'anonymous';
    return jwt.slice(-24);
  }

  private static appendClientIdQueryParam(apiUrl: string, clientId?: string) {
    if (!clientId) return apiUrl;
    const separator = apiUrl.includes('?') ? '&' : '?';
    return `${apiUrl}${separator}clientId=${encodeURIComponent(clientId)}`;
  }

  private static getTenantScopedHeaders(
    jwt?: string,
    explicitClientId?: string,
    options?: { includeClientHeader?: boolean }
  ) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (jwt) {
      headers.Authorization = `Bearer ${jwt}`;
    }
    const resolvedClientId = getActiveClientIdForApi(explicitClientId);
    const includeClientHeader = options?.includeClientHeader ?? true;
    const runtimeHost =
      typeof window !== 'undefined' ? window.location.hostname : '';
    const omitClientHeaderOnLocalhost =
      process.env.REACT_APP_OMIT_CLIENT_HEADER_ON_LOCALHOST === 'true';
    const shouldSendClientHeader =
      includeClientHeader &&
      resolvedClientId &&
      (!isLocalDevelopmentHost(runtimeHost) || !omitClientHeaderOnLocalhost);
    if (shouldSendClientHeader) {
      headers['X-OrgCloud-ClientId'] = resolvedClientId;
    }
    return headers;
  }

  private static getCachedRequest<T>(
    cache: Map<string, { expiresAt: number; request: Promise<T> }>,
    key: string,
    factory: () => Promise<T>,
    ttlMs: number = ApiService.SHORT_TTL_MS
  ): Promise<T> {
    const now = Date.now();
    const cached = cache.get(key);
    if (cached && cached.expiresAt > now) {
      return cached.request;
    }
    const request = factory().catch((error) => {
      cache.delete(key);
      throw error;
    });
    cache.set(key, { expiresAt: now + ttlMs, request });
    return request;
  }

  private static wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private static isTransientLocalGatewayError(error: any): boolean {
    const status = Number(error?.response?.status || 0);
    const code = String(error?.code || error?.response?.data?.error || '').toUpperCase();
    const message = String(error?.message || error?.response?.data?.message || '').toLowerCase();
    return (
      status === 500 ||
      status === 502 ||
      status === 503 ||
      code === 'ECONNREFUSED' ||
      code === 'ECONNRESET' ||
      code === 'ETIMEDOUT' ||
      message.includes('econnrefused') ||
      message.includes('local api proxy')
    );
  }

  private static async withLocalGatewayRetry<T>(
    factory: () => Promise<T>,
    attempts: number = 8
  ): Promise<T> {
    let lastError: any;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        return await factory();
      } catch (error) {
        lastError = error;
        if (attempt >= attempts - 1 || !ApiService.isTransientLocalGatewayError(error)) {
          throw error;
        }
        await ApiService.wait(Math.min(450 * (attempt + 1), 2500));
      }
    }
    throw lastError;
  }

  static accessUser(jwt: string | undefined) {
    // Verificar si el JWT está presente
    if (!jwt) {
      console.error('Error: JWT no proporcionado');
      return Promise.reject('JWT no proporcionado');
    }

    // URL base de la API Gateway
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessUser`;

    // Datos a enviar (puedes ajustarlos según tus necesidades)

    try {
      const data = {}
      // Enviar la solicitud POST a la API Gateway con el JWT en el encabezado de autorización
      const response = axios.post(apiUrl, data, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
      });

      // Devolver la respuesta de la API Gateway
      return response;
    } catch (error) {
      console.error("[ApiService.accessUser] Error al enviar datos a la API Gateway:", safeErrorDiagnostic(error));
      return Promise.reject('Error al enviar datos a la API Gateway');
    }
  }

  static async createObject(jwt: string | undefined, formData: ObjectType) {
    try {
      console.log("[ApiService.createObject] Enviando datos:");
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessObject`;
        const resolvedClientId = getActiveClientIdForApi((formData as any)?.ClientId);
        const response = await axios.post(apiUrl, formData, {
            headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
        });

        console.log("[ApiService.createObject] Respuesta completa:");
        console.log("[ApiService.createObject] response.data:");
        console.log("[ApiService.createObject] response.data.Item:");
        
        // La respuesta puede venir en diferentes formatos:
        // 1. { Item: {...} } - Formato DynamoDB
        // 2. { EntryId: "...", ... } - Objeto directo
        // 3. { message: "...", Item: {...} } - Respuesta con mensaje
        const responseData = response.data as any;
        const object = responseData?.Item || responseData;
        
        // Si el objeto tiene EntryId, devolverlo
        if (object && object.EntryId) {
          console.log("[ApiService.createObject] Objeto creado con EntryId:");
          return object;
        }
        
        // Si no tiene EntryId pero tiene otros campos, intentar usar el formData original con el EntryId generado
        // o devolver lo que venga
        console.warn("[ApiService.createObject] La respuesta no tiene EntryId, devolviendo respuesta completa:");
        return object || responseData;
    } catch (error) {
        console.error("[ApiService.createObject] Error sending data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async editObject(jwt: string | undefined, formData: ObjectType) {
    try {
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessObject`;
        const resolvedClientId = getActiveClientIdForApi((formData as any)?.ClientId);
        const response = await axios.patch(apiUrl, formData, {
            headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
        });

        console.log("[ApiService.editObject] Data sent successfully:");
        return response.data;
    } catch (error) {
        console.error("[ApiService.editObject] Error sending data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async deleteRow(jwt: string | undefined, rowData: { EntryId: string; ObjectId: string; ClientId?: string }) {
    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessRow?ObjectId=${rowData.ObjectId}&EntryId=${rowData.EntryId}`;
      const response = await axios.delete(apiUrl, {
        headers: ApiService.getTenantScopedHeaders(jwt, rowData.ClientId || getActiveClientIdForApi()),
        data: rowData // Algunos servidores requieren el body en DELETE
      });

      console.log("[ApiService.deleteRow] Row deleted successfully:");
      return response.data;
    } catch (error: any) {
      console.error("[ApiService.deleteRow] Error deleting row:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async deleteObject(jwt: string | undefined, entryId: string) {
    try {
        // Asegúrate de que entryId esté bien formateado y sea seguro para usar en la URL
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessObject`; // Añadir entryId a la URL

        // Hacer la solicitud DELETE con entryId en el cuerpo
        const response = await axios.delete(apiUrl, {
            headers: {
                'Authorization': `Bearer ${jwt}`,
                'Content-Type': 'application/json'
            },
            data: {
                EntryId: entryId // Incluye EntryId en el cuerpo de la solicitud
            }
        });

        console.log("[ApiService.deleteObject] Data deleted successfully:"); // Muestra la respuesta real
        return response.data;
    } catch (error) {
        console.error("[ApiService.deleteObject] Error deleting data:", safeErrorDiagnostic(error));
        throw error; // Lanza el error para que se maneje en otro lugar si es necesario
    }
  }

  static async deletePage(jwt: string | undefined, entryId: string) {
    try {
        // Asegúrate de que entryId esté bien formateado y sea seguro para usar en la URL
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPage`; // Añadir entryId a la URL

        // Hacer la solicitud DELETE con entryId en el cuerpo
        const response = await axios.delete(apiUrl, {
            headers: {
                'Authorization': `Bearer ${jwt}`,
                'Content-Type': 'application/json'
            },
            data: {
                EntryId: entryId // Incluye EntryId en el cuerpo de la solicitud
            }
        });

        console.log("[ApiService.deletePage] Data deleted successfully:"); // Muestra la respuesta real
        return response.data;
    } catch (error) {
        console.error("[ApiService.deletePage] Error deleting data:", safeErrorDiagnostic(error));
        throw error; // Lanza el error para que se maneje en otro lugar si es necesario
    }
  }

  static async readObjects(jwt: string | undefined, includeAll: boolean = false, clientId?: string) {
    try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      let apiUrl = `${ApiService.apiGatewayBaseUrl}accessObject`;
      if (includeAll) {
        apiUrl += '?includeAll=true';
      }
      apiUrl = ApiService.appendClientIdQueryParam(apiUrl, resolvedClientId);
      
      const cacheKey = `${ApiService.getShortJwtKey(jwt)}::${resolvedClientId || 'default'}::${includeAll ? 'all' : 'allowed'}`;
      const response = await ApiService.getCachedRequest(
        ApiService.objectSchemasRequestCache,
        cacheKey,
        () => axios.get(apiUrl, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
          timeout: 30000,
        }),
        30000
      );
  
      console.log("[ApiService.readObjects] Data obtained successfully:");
      return response.data; // Retorna los datos obtenidos si es necesario
    } catch (error: any) {
      // Manejar errores específicos
      if (error.response) {
        const status = error.response.status;
        
        if (status === 502 || status === 503 || status === 504) {
          // Bad Gateway, Service Unavailable, Gateway Timeout
          console.warn("[ApiService.readObjects] Error [redacted] ([redacted]): El servidor no está disponible temporalmente. Esto puede ser un problema temporal del backend.", safeErrorDiagnostic(error));
          // Retornar array vacío en lugar de lanzar error para que la app continúe funcionando
          return [];
        } else if (status === 401) {
          console.error('[ApiService.readObjects] Error 401: No autorizado. Verifica tu token JWT.');
          throw error;
        } else if (status === 404) {
          console.warn('[ApiService.readObjects] Error 404: Endpoint no encontrado. Retornando array vacío.');
          return [];
        } else {
          console.error("[ApiService.readObjects] Error [redacted] ([redacted]):", safeErrorDiagnostic(error));
          throw error;
        }
      } else if (error.request) {
        // La petición se hizo pero no hubo respuesta
        console.error("[ApiService.readObjects] Error de red: No se recibió respuesta del servidor.", safeErrorDiagnostic(error));
        // Retornar array vacío para que la app continúe funcionando
        return [];
      } else {
        // Error al configurar la petición
        console.error("[ApiService.readObjects] Error al configurar la petición:", safeErrorDiagnostic(error));
        throw error;
      }
    }
  }

static async readObjectSchema(jwt: string, EntryId: string | undefined, readSchema: boolean = true, clientId?: string) {
  try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      let apiUrl = `${ApiService.apiGatewayBaseUrl}accessObject?ObjectId=${EntryId}`;
      if (readSchema) {
          apiUrl += '&ReadSchema=true';
      }
      apiUrl = ApiService.appendClientIdQueryParam(apiUrl, resolvedClientId);
      const cacheKey = `${ApiService.getShortJwtKey(jwt)}::${resolvedClientId || 'default'}::${EntryId || 'none'}::${readSchema ? 'schema' : 'object'}`;
      const response = await ApiService.getCachedRequest(
        ApiService.objectSchemasRequestCache,
        cacheKey,
        () => axios.get(apiUrl, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
          timeout: 30000,
        }),
        60000
      );

      console.log("[ApiService.readObjectSchema] Schema retrieved successfully:");
      
      // La respuesta puede venir en diferentes formatos
      let schemaData = response.data;
      
      // Si es un array, buscar el objeto con el EntryId correcto
      if (Array.isArray(schemaData) && schemaData.length > 0) {
        if (EntryId) {
          // Buscar el objeto con el EntryId solicitado
          const foundObject = schemaData.find((obj: any) => obj.EntryId === EntryId);
          if (foundObject) {
            schemaData = foundObject;
          } else {
            console.warn("[ApiService.readObjectSchema] No se encontró objeto con EntryId [redacted], usando el primero del array");
            schemaData = schemaData[0];
          }
        } else {
          // Si no hay EntryId, usar el primer elemento (comportamiento legacy para CommonObjects)
          schemaData = schemaData[0];
        }
      }
      
      // Si tiene Item, usar Item
      if (schemaData?.Item) {
        schemaData = schemaData.Item;
      }
      
      // Si tiene Schema, usar Schema
      if (schemaData?.Schema) {
        schemaData = schemaData.Schema;
      }
      
      return schemaData;
  } catch (error: any) {
      // Manejar errores específicos
      if (error.response) {
        const status = error.response.status;
        
        if (status === 404) {
          console.warn("[ApiService.readObjectSchema] Error 404 (Not Found): El objeto con EntryId [redacted] no existe en Object table.", safeErrorDiagnostic(error));
          console.log(`[ApiService.readObjectSchema] Intentando buscar en CommonObjects como fallback...`);
          
          // Intentar buscar en CommonObjects como fallback
          try {
            // El lambda accessObject ya debería buscar en CommonObjects automáticamente,
            // pero si aún así falla, retornar null de forma suave
            console.warn("[ApiService.readObjectSchema] El objeto [redacted] no se encontró ni en Object ni en CommonObjects.", safeErrorDiagnostic(error));
            return null;
          } catch (fallbackError) {
            console.error("[ApiService.readObjectSchema] Error en fallback a CommonObjects:", safeErrorDiagnostic(fallbackError));
            return null;
          }
        } else if (status === 403) {
          console.error("[ApiService.readObjectSchema] Error 403 (Forbidden): No tienes permisos para leer el objeto [redacted].", safeErrorDiagnostic(error));
          throw error;
        } else if (status === 401) {
          console.error(`[ApiService.readObjectSchema] Error 401 (Unauthorized): No autorizado. Verifica tu token JWT.`);
          throw error;
        } else if (status === 502 || status === 503 || status === 504) {
          console.warn("[ApiService.readObjectSchema] Error [redacted] ([redacted]): El servidor no está disponible temporalmente.", safeErrorDiagnostic(error));
          return null; // Retornar null para manejo suave
        } else {
          console.error("[ApiService.readObjectSchema] Error [redacted] ([redacted]):", safeErrorDiagnostic(error));
        }
      } else if (error.request) {
        console.error("[ApiService.readObjectSchema] Error de red: No se recibió respuesta del servidor.", safeErrorDiagnostic(error));
      } else {
        console.error("[ApiService.readObjectSchema] Error al configurar la petición:", safeErrorDiagnostic(error));
      }
      
      // Retornar null en lugar de lanzar error para permitir manejo más suave
      return null;
  }
}

static async createRow(jwt: string, ObjectId: string, data: { [key: string]: any }, clientId?: string) {
    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessRow`;
      const resolvedClientId = clientId || getActiveClientIdForApi();
  
      const payload = {
        ObjectId,
        Data: data,
      };
  
      const response = await axios.post(apiUrl, payload, {
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
      });
  
      ApiService.rowsInFlight.clear();
      console.log("[ApiService.createRow] Data retrieved successfully:");
      return response.data;
    } catch (error) {
      console.error("[ApiService.createRow] Error retrieving data:", safeErrorDiagnostic(error));
      throw error;
    }
  }  
  

static async readRows(
    jwt: string,
    ObjectId: string | undefined,
    limit: number = 5,
    lastEvaluatedKey: string | null = null,
    clientId?: string
  ) {
    try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      let apiUrl = `${ApiService.apiGatewayBaseUrl}accessRow?ObjectId=${ObjectId}&limit=${limit}`;
      if (lastEvaluatedKey) {
        apiUrl += `&last_evaluated_key=${encodeURIComponent(lastEvaluatedKey)}`;
      }
  
      const requestKey = `${ApiService.getShortJwtKey(jwt)}::${resolvedClientId || 'default'}::${apiUrl}`;
      const existingRequest = ApiService.rowsInFlight.get(requestKey);
      if (existingRequest) {
        return existingRequest;
      }

      const pendingRequest = axios.get(apiUrl, {
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
        timeout: 30000,
      })
        .then((response) => {
          console.log("[ApiService.readRows] Rows retrieved successfully:");
          return response.data;
        })
        .finally(() => {
          if (ApiService.rowsInFlight.get(requestKey) === pendingRequest) {
            ApiService.rowsInFlight.delete(requestKey);
          }
        });

      ApiService.rowsInFlight.set(requestKey, pendingRequest);
      return await pendingRequest;
    } catch (error: any) {
      // Manejar errores específicos
      if (error.response) {
        const status = error.response.status;
        
        if (status === 403) {
          console.error("[ApiService.readRows] Error 403 (Forbidden): No tienes permisos para leer datos del objeto [redacted].", safeErrorDiagnostic(error));
          console.error(`[ApiService.readRows] Si estás intentando leer datos de un CommonObject, necesitas usar un objeto del cliente que lo extienda.`);
          // Lanzar error con mensaje más descriptivo
          const forbiddenError = new Error(`No tienes permisos para leer datos del objeto ${ObjectId}. Si es un CommonObject, usa un objeto del cliente que lo extienda.`);
          (forbiddenError as any).status = 403;
          (forbiddenError as any).isForbidden = true;
          throw forbiddenError;
        } else if (status === 404) {
          console.warn("[ApiService.readRows] Error 404: Objeto [redacted] no encontrado.", safeErrorDiagnostic(error));
          throw error;
        } else if (status === 502 || status === 503 || status === 504) {
          console.warn("[ApiService.readRows] Error [redacted] ([redacted]): El servidor no está disponible temporalmente.", safeErrorDiagnostic(error));
          throw error;
        } else {
          console.error("[ApiService.readRows] Error [redacted] ([redacted]):", safeErrorDiagnostic(error));
          throw error;
        }
      } else if (error.request) {
        console.error("[ApiService.readRows] Error de red: No se recibió respuesta del servidor.", safeErrorDiagnostic(error));
        throw error;
      } else {
        console.error("[ApiService.readRows] Error al configurar la petición:", safeErrorDiagnostic(error));
        throw error;
      }
    }
  }  

static async updateRows(jwt: string, form_data: RowType) {
    try {
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessRow`;
        const resolvedClientId = getActiveClientIdForApi(form_data.ClientId);
        console.log("🔵 ApiService.updateRows - Iniciando PATCH:");
        
        const response = await axios.patch(apiUrl, form_data, {
            headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
        });
  
        ApiService.rowsInFlight.clear();
        console.log("✅ ApiService.updateRows - PATCH exitoso:");
        return response.data;
    } catch (error: any) {
        console.error("❌ ApiService.updateRows - Error en PATCH:", safeErrorDiagnostic(error));
        throw error;
    }
  }

static async updateOwnSportsAvatar(
  jwt: string,
  row: Pick<RowType, 'EntryId' | 'ObjectId' | 'ClientId'>,
  data: Record<string, any>
) {
  const apiUrl = `${ApiService.apiGatewayBaseUrl}accessRow`;
  const resolvedClientId = getActiveClientIdForApi(row.ClientId);
  const response = await axios.patch(apiUrl, {
    ...row,
    UpdateMode: 'selfSportsAvatar',
    Data: data,
  }, {
    headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
  });
  return response.data;
}

static async readPages(jwt: string, dshid?: string, clientId?: string) {
  try {
    const resolvedClientId = getActiveClientIdForApi(clientId);
    let apiUrl: string; // Declarar apiUrl fuera del condicional
    if (dshid) {
      apiUrl = `${ApiService.apiGatewayBaseUrl}accessPage?PageId=${dshid}`;
    } else {
      apiUrl = `${ApiService.apiGatewayBaseUrl}accessPage`;
    }
      apiUrl = ApiService.appendClientIdQueryParam(apiUrl, resolvedClientId);
      const cacheKey = `${ApiService.getShortJwtKey(jwt)}::${resolvedClientId || 'default'}::${apiUrl}`;
      const response = await ApiService.getCachedRequest(
        ApiService.pagesRequestCache,
        cacheKey,
        () => ApiService.withLocalGatewayRetry(() =>
          axios.get(apiUrl, {
            headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
          })
        )
      );

      console.log("[ApiService.readPages] Data retrieved successfully:"); // Aquí puedes acceder a los datos de la respuesta
      
      // Si la respuesta es un string (error del backend), parsearlo
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          // Si no es JSON válido, puede ser un mensaje de error
          console.error("[ApiService.readPages] Error response from backend:", safeErrorDiagnostic(e));
          throw new Error(data || 'Error retrieving pages data');
        }
      }
      
      // Si es un array, procesar cada página
      if (Array.isArray(data)) {
        data = data.map((page: any) => {
          if (page?.Data?.components) {
            page.Data.components = page.Data.components.map((comp: any) => {
              // Si formRequest es string, parsearlo a objeto para que el backend pueda procesarlo
              // Nota: Esto es una solución temporal. El backend debería manejar ambos casos.
              if (comp.formRequest && typeof comp.formRequest === 'string') {
                try {
                  comp.formRequest = JSON.parse(comp.formRequest);
                } catch (e) {
                  console.warn("[ApiService.readPages] Error parseando formRequest en componente [redacted]:", safeErrorDiagnostic(e));
                  comp.formRequest = {};
                }
              }
              return comp;
            });
          }
          return page;
        });
      } else if (data?.Data?.components) {
        // Si es un solo objeto de página
        data.Data.components = data.Data.components.map((comp: any) => {
          if (comp.formRequest && typeof comp.formRequest === 'string') {
            try {
              comp.formRequest = JSON.parse(comp.formRequest);
            } catch (e) {
              console.warn("[ApiService.readPages] Error parseando formRequest en componente [redacted]:", safeErrorDiagnostic(e));
              comp.formRequest = {};
            }
          }
          return comp;
        });
      }
      
      return data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error: any) {
      console.error("[ApiService.readPages] Error retrieving pages data:", safeErrorDiagnostic(error));
      // Si el error viene del backend con un mensaje específico
      if (error?.response?.data) {
        const errorData = error.response.data;
        const errorMessage = typeof errorData === 'string' ? errorData : errorData.message || errorData.error || JSON.stringify(errorData);
        console.error("[ApiService.readPages] Backend error message:", safeErrorDiagnostic(error));
        throw Object.assign(new Error(errorMessage), { status: error.response.status });
      }
      throw error;
  }
}

static async queryData(jwt: string, form_data: Record<string, any>, clientId?: string) {
  try {
      // Normalizar requests legacy para no romper pages/features guardados con shape antiguo.
      const normalizedData =
        normalizeComponentFormRequest(form_data) || (form_data as RequestData);

      // Limpiar y validar los datos antes de enviarlos
      const cleanedData = cleanRequestData(normalizedData as RequestData);
      
      if (!cleanedData) {
          throw new Error('Invalid request data: ObjectId is required');
      }

      const validation = validateRequestData(cleanedData);
      if (!validation.valid) {
          throw new Error(`Invalid request data: ${validation.error}`);
      }

      const resolvedClientId = getActiveClientIdForApi(clientId || form_data?.ClientId || form_data?.clientId);
      const requestData = resolvedClientId
        ? { ...cleanedData, ClientId: resolvedClientId, clientId: resolvedClientId }
        : cleanedData;

      console.log("[ApiService.queryData] Sending query data:");

      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessQueryData`;
      const requestKey = JSON.stringify({
        apiUrl,
        clientId: resolvedClientId || '',
        jwt,
        requestData,
      });
      const existingRequest = ApiService.queryDataInFlight.get(requestKey);
      if (existingRequest) {
        return existingRequest;
      }

      const pendingRequest = ApiService.withLocalGatewayRetry(() =>
        axios.post(apiUrl, requestData, {
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json'
          },
        })
      )
        .then((response) => {
          console.log("[ApiService.queryData] Data retrieved successfully:");
          return response.data;
        })
        .finally(() => {
          if (ApiService.queryDataInFlight.get(requestKey) === pendingRequest) {
            ApiService.queryDataInFlight.delete(requestKey);
          }
        });

      ApiService.queryDataInFlight.set(requestKey, pendingRequest);
      return pendingRequest;
  } catch (error: any) {
      if (error.response) {
          console.error("[ApiService.queryData] Backend error:", safeErrorDiagnostic(error));
          throw new Error(`Backend error: ${JSON.stringify(error.response.data)}`);
      }
      console.warn("[ApiService.queryData] Request unavailable:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async createPage(jwt: string, form_data: PageConsolidatedData) {
  try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPage`;
      const response = await axios.post(apiUrl, form_data,{
          headers: {
              'Authorization': `Bearer ${jwt}`,
              'Content-Type': 'application/json'
          },
      });

      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.createPage] Error retrieving data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async updatePage(jwt: string, form_data: PageConsolidatedData) {
    try {
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPage`;
        const response = await axios.patch(apiUrl, form_data,{
            headers: {
                'Authorization': `Bearer ${jwt}`,
                'Content-Type': 'application/json'
            },
        });
  
        return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
    } catch (error) {
        console.error("[ApiService.updatePage] Error retrieving data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

static async readClient(jwt: string, clientId?: string) {
  try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      let apiUrl = `${ApiService.apiGatewayBaseUrl}accessClient`;
      // Con varios clientes el backend exige clientId; usar el explícito o el último cliente de la sesión (localStorage)
      if (resolvedClientId) {
          apiUrl += `?clientId=${encodeURIComponent(resolvedClientId)}`;
      }
      const config: RetriableRequestConfig = {
          _skipAuthRedirect: true,
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
      } as RetriableRequestConfig;
      const response = await axios.get(apiUrl, config);

      // Verificar si la respuesta es HTML
      if (typeof response.data === 'string' && response.data.trim().startsWith('<!DOCTYPE')) {
          console.error('[readClient] Respuesta HTML recibida en lugar de JSON');
          throw new Error('El servidor devolvió HTML en lugar de JSON. Verifica la configuración del API Gateway y que el endpoint accessClient esté correctamente configurado.');
      }

      console.log("[ApiService.readClient] Data retrieved successfully:"); // Aquí puedes acceder a los datos de la respuesta
      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error: any) {
      console.error("[ApiService.readClient] Error retrieving data:", safeErrorDiagnostic(error));
      
      // Verificar si es un error de respuesta HTML
      if (error.isHtmlResponse || (error.response?.data && typeof error.response.data === 'string' && error.response.data.trim().startsWith('<!DOCTYPE'))) {
          throw new Error('El servidor devolvió HTML en lugar de JSON. Esto puede indicar un problema de routing o configuración del API Gateway. Verifica que el endpoint accessClient esté correctamente configurado.');
      }
      
      // Proporcionar mensajes de error más descriptivos
      if (error.response) {
          const status = error.response.status;
          const errorMessage = error.response.data || error.response.statusText;
          
          if (status === 404) {
              // 404 puede significar que el usuario no tiene ClientId o que el cliente no existe
              const message = typeof errorMessage === 'string' 
                  ? errorMessage 
                  : (errorMessage?.error || 'Cliente no encontrado. Verifica que tu usuario esté asociado a una empresa.');
              throw new Error(`Error 404: ${message}`);
          } else if (status === 401) {
              throw new Error('No autorizado. Verifica tu token de autenticación.');
          } else if (status === 500) {
              throw new Error('Error del servidor. Por favor, intenta nuevamente más tarde.');
          } else {
              throw new Error(`Error ${status}: ${typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)}`);
          }
      } else if (error.request) {
          // La petición se hizo pero no hubo respuesta
          throw new Error('No se pudo conectar con el servidor. Verifica tu conexión a internet.');
      } else {
          // Error al configurar la petición
          throw new Error(`Error al realizar la petición: ${error.message}`);
      }
  }
}

/**
 * Obtiene todos los clientes asociados a un usuario
 * Usado cuando un usuario puede pertenecer a múltiples organizaciones
 * 
 * @param jwt - Token de autenticación
 * @returns Lista de clientes del usuario con información básica
 */
static async readUserClients(jwt: string): Promise<{ 
  Clients: Array<{
    ClientId: string;
    Name: string;
    Subdomain: string;
    Logo?: string;
    CustomAppHostname?: string;
  }>;
  HasMultiple: boolean;
}> {
  try {
      if (!jwt || !String(jwt).trim() || String(jwt).trim() === 'undefined') {
        TokenManager.clearSession();
        TokenManager.redirectToLogin();
        throw new Error('No hay una sesion activa para cargar los clientes del usuario.');
      }
      // Usar el endpoint accessUser con action=clients
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessUser?action=clients`;
      const response = await axios.get(apiUrl, {
          headers: {
              'Authorization': `Bearer ${jwt}`,
              'Content-Type': 'application/json'
          },
      });

      // Verificar si la respuesta es HTML
      if (typeof response.data === 'string' && response.data.trim().startsWith('<!DOCTYPE')) {
          console.error('[readUserClients] Respuesta HTML recibida en lugar de JSON');
          throw new Error('El servidor devolvió HTML en lugar de JSON. Verifica la configuración del API Gateway y que el endpoint accessUser esté correctamente configurado.');
      }

      console.log("[ApiService.readUserClients] User clients retrieved successfully:");
      
      // El backend devuelve { clients: [...] }
      const clients = response.data.clients || [];
      
      // Obtener el idioma actual del usuario
      const currentLanguage = localStorage.getItem('i18nextLng') || 'es';
      
      console.log("[ApiService.readUserClients] [readUserClients] Idioma actual:");
      console.log("[ApiService.readUserClients] [readUserClients] Clientes recibidos:");
      
      // Transformar los datos del backend al formato esperado
      const transformedClients = clients.map((client: any) => {
        // Extraer el nombre del cliente (puede ser LocalizedText)
        const nameField = client.Information?.General?.Name || 
                         client.Information?.Name ||
                         client.Name;
        
        console.log("[ApiService.readUserClients] [readUserClients] Cliente:");
        console.log("[ApiService.readUserClients] [readUserClients] nameField:");
        console.log("[ApiService.readUserClients] [readUserClients] tipo de nameField:");
        
        // Nombre para mostrar: General.Name localizado; si falta, UniqueName con primera letra en mayúscula
        const uniqueRaw = (client.UniqueName || '').trim();
        const fromLocalized = getLocalizedText(nameField, '', currentLanguage).trim();
        const clientName = fromLocalized
          ? fromLocalized
          : uniqueRaw
            ? formatUniqueNameForDisplay(uniqueRaw)
            : 'Sin nombre';
        console.log("[ApiService.readUserClients] [readUserClients] clientName procesado:");
        
        // El subdominio se guarda con el EntryId 'b1e2f8c3-4d6a-49d8-b3a7-1f2e0c5d7a9e'
        // Buscar en Information.General por ese EntryId o por campos comunes
        const generalInfo = client.Information?.General || {};
        const domainFieldId = 'b1e2f8c3-4d6a-49d8-b3a7-1f2e0c5d7a9e';
        const logoFieldId = 'd3cc5d64-b089-422e-9d4b-0172d25bae2a';
        
        // Buscar el subdominio por EntryId o por nombre de campo
        let subdomain = generalInfo[domainFieldId] || 
                       generalInfo.Domain || 
                       generalInfo.Subdomain ||
                       '';
        
        // Si el subdominio contiene ".orgcloud.io", extraer solo la parte del subdominio
        if (subdomain && subdomain.includes('.')) {
          subdomain = subdomain.split('.')[0];
        }

        // Si no hay campo de dominio en General, usar UniqueName (minúsculas) para la URL mostrada
        if (!String(subdomain || '').trim() && uniqueRaw) {
          subdomain = uniqueRaw.toLowerCase();
        }
        
        // Extraer el logo por EntryId o por nombre de campo
        let logo = generalInfo[logoFieldId] || 
                  generalInfo.Logo || 
                  '';
        
        // Si el logo es una ruta relativa, convertirla a URL completa
        if (logo && logo.startsWith('files/clients/public/')) {
          logo = `https://orgcloudimages-20250323-abc123.s3.eu-west-3.amazonaws.com/${logo}`;
        }
        
        return {
          ClientId: client.EntryId,
          Name: clientName,
          Subdomain: subdomain,
          Logo: logo,
          CustomAppHostname: normalizeCustomAppHostname(client.Information?.CustomAppHostname),
        };
      });
      
      return {
        Clients: transformedClients,
        HasMultiple: transformedClients.length > 1
      };
  } catch (error: any) {
      console.error("[ApiService.readUserClients] Error retrieving user clients:", safeErrorDiagnostic(error));
      
      // Verificar si es un error de respuesta HTML
      if (error.isHtmlResponse || (error.response?.data && typeof error.response.data === 'string' && error.response.data.trim().startsWith('<!DOCTYPE'))) {
          throw new Error('El servidor devolvió HTML en lugar de JSON. Esto puede indicar un problema de routing o configuración del API Gateway. Verifica que el endpoint accessUser esté correctamente configurado.');
      }
      
      if (error.response) {
          const status = error.response.status;
          if (status === 404) {
              // No hay clientes asociados
              return {
                Clients: [],
                HasMultiple: false
              };
          }
      }
      throw error;
  }
}

/**
 * Obtiene un cliente específico por su ID
 * Usado para cargar la información de un cliente seleccionado
 * 
 * @param jwt - Token de autenticación  
 * @param clientId - ID del cliente a obtener
 * @returns Información del cliente
 */
static async readClientById(jwt: string, clientId: string) {
  try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessClient?clientId=${clientId}`;
      const response = await axios.get(apiUrl, {
          headers: ApiService.getTenantScopedHeaders(jwt, clientId, { includeClientHeader: false }),
      });

      console.log("[ApiService.readClientById] Client retrieved successfully:");
      return response.data;
  } catch (error: any) {
      console.error("[ApiService.readClientById] Error retrieving client by ID:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async updateClient(jwt: string, formData: Partial<ClientType>) {
    try {
        // Si el formData tiene EntryId, pasarlo como clientId query parameter
        // Esto es crítico para que el backend sepa qué cliente actualizar,
        // especialmente cuando un admin de OrgCloud edita un cliente diferente al suyo
        const clientId = formData.EntryId || getActiveClientIdForApi();
        const apiUrl = clientId
            ? `${ApiService.apiGatewayBaseUrl}accessClient?clientId=${encodeURIComponent(clientId)}`
            : `${ApiService.apiGatewayBaseUrl}accessClient`;

        // Realizar la solicitud
        const response = await axios.patch(apiUrl, formData, {
            headers: ApiService.getTenantScopedHeaders(jwt, clientId),
        });

        return response.data;
    } catch (error) {
        console.error("[ApiService.updateClient] Error sending data:", safeErrorDiagnostic(error));
        throw error;
    }
}

static async testIntegration(jwt: string, payload: {
    clientId?: string;
    providerId: string;
    operation: 'connection' | 'message';
    config?: Record<string, any>;
    recipient?: string;
    message?: string;
}) {
    const clientId = payload.clientId || getActiveClientIdForApi();
    const apiUrl = clientId
        ? `${ApiService.apiGatewayBaseUrl}accessClient?action=testIntegration&clientId=${encodeURIComponent(clientId)}`
        : `${ApiService.apiGatewayBaseUrl}accessClient?action=testIntegration`;
    const response = await axios.post(apiUrl, payload, {
        headers: ApiService.getTenantScopedHeaders(jwt, clientId),
    });
    return response.data;
}

static async listHealthSportProviders(jwt: string, clientId?: string) {
    const resolvedClientId = getActiveClientIdForApi(clientId);
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessHealthSportIntegrations/providers?ClientId=${encodeURIComponent(resolvedClientId || '')}`;
    const response = await axios.get(apiUrl, {
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
    });
    return response.data;
}

static async listHealthSportConnections(jwt: string, clientId?: string, memberId?: string) {
    const resolvedClientId = getActiveClientIdForApi(clientId);
    const params = new URLSearchParams();
    params.set('ClientId', resolvedClientId || '');
    if (memberId) params.set('member', memberId);
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessHealthSportIntegrations/connections?${params.toString()}`;
    const response = await axios.get(apiUrl, {
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
    });
    return response.data;
}

static async listHealthSportSummary(jwt: string, clientId?: string, memberId?: string, limit: number = 20) {
    const resolvedClientId = getActiveClientIdForApi(clientId);
    const params = new URLSearchParams();
    params.set('ClientId', resolvedClientId || '');
    params.set('limit', String(limit));
    if (memberId) params.set('member', memberId);
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessHealthSportIntegrations/summary?${params.toString()}`;
    const response = await axios.get(apiUrl, {
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
    });
    return response.data;
}

static async startHealthSportOAuth(jwt: string, payload: {
    clientId?: string;
    provider: string;
    redirectUri?: string;
    scope?: string;
    state?: string;
    member?: string;
}) {
    const clientId = payload.clientId || getActiveClientIdForApi();
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessHealthSportIntegrations/oauth/start`;
    const response = await axios.post(apiUrl, { ...payload, ClientId: clientId }, {
        headers: ApiService.getTenantScopedHeaders(jwt, clientId),
    });
    return response.data;
}

static async completeHealthSportOAuth(jwt: string, payload: {
    clientId?: string;
    provider: string;
    code: string;
    state?: string;
    scope?: string;
    member?: string;
    redirectUri?: string;
}) {
    const clientId = payload.clientId || getActiveClientIdForApi();
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessHealthSportIntegrations/oauth/callback`;
    const response = await axios.post(apiUrl, { ...payload, ClientId: clientId }, {
        headers: ApiService.getTenantScopedHeaders(jwt, clientId),
    });
    return response.data;
}

static async syncHealthSportConnection(jwt: string, payload: {
    clientId?: string;
    provider?: string;
    member?: string;
    memberId?: string;
    connectionId?: string;
    windowDays?: number;
    after?: string;
    before?: string;
    limit?: number;
    providerPayload?: Record<string, any>;
}) {
    const clientId = payload.clientId || getActiveClientIdForApi();
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessHealthSportIntegrations/sync`;
    const response = await axios.post(apiUrl, { ...payload, ClientId: clientId }, {
        headers: ApiService.getTenantScopedHeaders(jwt, clientId),
    });
    return response.data;
}

static async importHealthSportActivity(jwt: string, payload: {
    clientId?: string;
    provider?: string;
    member?: string;
    memberId?: string;
    connectionId?: string;
    visibility?: string;
    activity?: Record<string, any>;
    fileName?: string;
    fileContent?: string;
    fileContentBase64?: string;
    format?: string;
}) {
    const clientId = payload.clientId || getActiveClientIdForApi();
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessHealthSportIntegrations/import-file`;
    const response = await axios.post(apiUrl, { ...payload, ClientId: clientId }, {
        headers: ApiService.getTenantScopedHeaders(jwt, clientId),
    });
    return response.data;
}

static async confirmHealthSportCandidate(jwt: string, payload: {
    clientId?: string;
    candidateId: string;
    decision?: 'confirmed' | 'rejected';
    visibility?: string;
}) {
    const clientId = payload.clientId || getActiveClientIdForApi();
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessHealthSportIntegrations/candidates/${encodeURIComponent(payload.candidateId)}/confirm`;
    const response = await axios.post(apiUrl, { ...payload, ClientId: clientId }, {
        headers: ApiService.getTenantScopedHeaders(jwt, clientId),
    });
    return response.data;
}

static async revokeHealthSportConnection(jwt: string, connectionId: string, clientId?: string) {
    const resolvedClientId = getActiveClientIdForApi(clientId);
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessHealthSportIntegrations/connections/${encodeURIComponent(connectionId)}?ClientId=${encodeURIComponent(resolvedClientId || '')}`;
    const response = await axios.delete(apiUrl, {
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
    });
    return response.data;
}

static async listAllClients(jwt: string) {
    try {
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessClient?listAll=true`;
        const response = await axios.get(apiUrl, {
            headers: ApiService.getTenantScopedHeaders(jwt),
        });

        console.log("[ApiService.listAllClients] All clients retrieved successfully:");
        return response.data;
    } catch (error: any) {
        console.error("[ApiService.listAllClients] Error retrieving all clients:", safeErrorDiagnostic(error));
        throw error;
    }
}

static async readUsers(jwt: string, clientId?: string) {
  try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      const apiUrl = ApiService.appendClientIdQueryParam(
        `${ApiService.apiGatewayBaseUrl}accessUser`,
        resolvedClientId,
      );
      const config: RetriableRequestConfig = {
          _skipAuthRedirect: true,
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
      } as RetriableRequestConfig;
      const response = await axios.get(apiUrl, config);

      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.readUsers] Error retrieving data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async readGroups(jwt: string, clientId?: string) {
  try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      const apiUrl = ApiService.appendClientIdQueryParam(
        `${ApiService.apiGatewayBaseUrl}accessUserManagement`,
        resolvedClientId,
      );
      const config: RetriableRequestConfig = {
          _skipAuthRedirect: true,
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
      } as RetriableRequestConfig;
      const response = await axios.get(apiUrl, config);

      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.readGroups] Error retrieving data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async createGroups(jwt: string, form_data: any) {
  try {
      const resolvedClientId = getActiveClientIdForApi(form_data?.ClientId);
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessUserManagement`;
      const response = await axios.post(apiUrl, form_data,{
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
    });

      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.createGroups] Error retrieving data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async updateGroup(jwt: string, form_data: any) {
  try {
      const resolvedClientId = getActiveClientIdForApi(form_data?.ClientId);
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessUserManagement`;
      const response = await axios.patch(apiUrl, form_data,{
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
    });
      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.updateGroup] Error retrieving data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async readObjectSchemas(jwt: string, clientId?: string) {
  try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      const ReadSchema = true
      // Agregar timestamp para evitar caché del navegador y API Gateway
      const apiUrl = ApiService.appendClientIdQueryParam(
        `${ApiService.apiGatewayBaseUrl}accessObject?ReadSchema=${ReadSchema}`,
        resolvedClientId,
      );
      console.log("[ApiService.readObjectSchemas] Llamando a:");
      const cacheKey = `${ApiService.getShortJwtKey(jwt)}::${resolvedClientId || 'default'}::${apiUrl}`;
      const response = await ApiService.getCachedRequest(
        ApiService.objectSchemasRequestCache,
        cacheKey,
        () => axios.get(apiUrl, {
          headers: {
            ...ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
        })
      );
      console.log("[ApiService.readObjectSchemas] Respuesta recibida:");
      
      // Asegurar que siempre devolvemos un array
      const data = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
      console.log("[ApiService.readObjectSchemas] Datos procesados:");
      return data;
  } catch (error: any) {
      console.error("[ApiService.readObjectSchemas] Error retrieving object schema data:", safeErrorDiagnostic(error));
      if (error.response) {
        console.error("[ApiService.readObjectSchemas] Error response:", safeErrorDiagnostic(error));
      }
      throw error;
  }
}

static deleteGroup(jwt: string | undefined, entryId: string) {
  try {
      const resolvedClientId = getActiveClientIdForApi();
      // Asegúrate de que entryId esté bien formateado y sea seguro para usar en la URL
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessUserManagement`; // Añadir entryId a la URL

      // Hacer la solicitud DELETE con entryId en el cuerpo
      return axios.delete(apiUrl, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
          data: {
              EntryId: entryId // Incluye EntryId en el cuerpo de la solicitud
          }
      });

  } catch (error) {
      console.error("[ApiService.deleteGroup] Error deleting data:", safeErrorDiagnostic(error));
      throw error; // Lanza el error para que se maneje en otro lugar si es necesario
  }
}

static async readDashboardSchemas(jwt: string, clientId?: string) {
  try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      const ReadSchema = true
      const apiUrl = ApiService.appendClientIdQueryParam(
        `${ApiService.apiGatewayBaseUrl}accessPage?ReadSchema=${ReadSchema}`,
        resolvedClientId,
      );
      const response = await axios.get(apiUrl, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
      });
      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.readDashboardSchemas] Error retrieving page schema data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async readSignUpManagement(jwt: string, id?: string, token?: string, clientId?: string) {
  try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      // Construye la URL condicionalmente
      let apiUrl = `${ApiService.apiGatewayBaseUrl}accessSignUpManagement`;
      const queryParams = [];
      if (id) queryParams.push(`FormId=${id}`);
      if (token) queryParams.push(`RequestToken=${token}`);
      if (queryParams.length > 0) {
          apiUrl += `?${queryParams.join('&')}`;
      }
      apiUrl = ApiService.appendClientIdQueryParam(apiUrl, resolvedClientId);

      const response = await axios.get(apiUrl, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
      });

      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.readSignUpManagement] Error retrieving page schema data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async createSignUpManagement(jwt: string, form_data: SignUpForm) {
  try {
      const resolvedClientId = getActiveClientIdForApi(form_data?.ClientId);
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessSignUpManagement`;
      const response = await axios.post(apiUrl, form_data, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
      });
      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.createSignUpManagement] Error retrieving page schema data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async updateSignUpManagement(jwt: string, form_data: SignUpForm) {
  try {
      const resolvedClientId = getActiveClientIdForApi(form_data?.ClientId);
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessSignUpManagement`;
      const response = await axios.patch(apiUrl, form_data, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
      });
      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.updateSignUpManagement] Error retrieving page schema data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static deleteSignUpManagement(jwt: string | undefined, entryId: string) {
  try {
      const resolvedClientId = getActiveClientIdForApi();
      // Asegúrate de que entryId esté bien formateado y sea seguro para usar en la URL
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessSignUpManagement`; // Añadir entryId a la URL

      // Hacer la solicitud DELETE con entryId en el cuerpo
      return axios.delete(apiUrl, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
          data: {
              EntryId: entryId // Incluye EntryId en el cuerpo de la solicitud
          }
      });

  } catch (error) {
      console.error("[ApiService.deleteSignUpManagement] Error deleting data:", safeErrorDiagnostic(error));
      throw error; // Lanza el error para que se maneje en otro lugar si es necesario
  }
}

static async readSignUp(id?: string, token?: string) {
  try {
      // Construye la URL condicionalmente
      let apiUrl = `${ApiService.apiGatewayBaseUrl}signup`;
      const queryParams = [];
      if (id) queryParams.push(`FormId=${id}`);
      if (token) queryParams.push(`RequestToken=${encodeURIComponent(token)}`);
      if (queryParams.length > 0) {
          apiUrl += `?${queryParams.join('&')}`;
      }

      const response = await axios.get(apiUrl, {
          headers: {
              'Content-Type': 'application/json'
          },
      });

      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.readSignUp] Error retrieving page schema data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async signUp(form: SignUpForm) {
  try {
      // Construye la URL condicionalmente
      let apiUrl = `${ApiService.apiGatewayBaseUrl}signup`;

      const response = await axios.post(apiUrl, form, {
          headers: {
              'Content-Type': 'application/json'
          },
      });
      console.log("[ApiService.signUp] Diagnostic event")
      return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
  } catch (error) {
      console.error("[ApiService.signUp] Error retrieving page schema data:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async checkSignUpEmail(formId: string, email: string, requestToken?: string) {
  try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}signup`;
      const response = await axios.post(apiUrl, {
          Action: 'check-email',
          FormId: formId,
          Email: String(email || '').trim().toLowerCase(),
          ...(requestToken ? { RequestToken: requestToken } : {}),
      }, {
          headers: {
              'Content-Type': 'application/json'
          },
      });
      return response.data;
  } catch (error) {
      console.error("[ApiService.checkSignUpEmail] Error checking signup email:", safeErrorDiagnostic(error));
      throw error;
  }
}

static async stripeClientLink(jwt: string, clientId?: string, subClientId?: string) {
    try {
        const resolvedClientId = getActiveClientIdForApi(clientId);
        const resolvedSubClientId = String(subClientId || '').trim();
        const searchParams = new URLSearchParams();
        if (resolvedClientId) searchParams.set('clientId', resolvedClientId);
        if (resolvedSubClientId) searchParams.set('subClientId', resolvedSubClientId);
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessStripeClient${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
        const response = await axios.post(apiUrl, {
          ...(resolvedClientId ? { clientId: resolvedClientId, ClientId: resolvedClientId } : {}),
          ...(resolvedSubClientId ? { subClientId: resolvedSubClientId, SubClientId: resolvedSubClientId } : {}),
        },{
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
      });
  
        return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
    } catch (error) {
        console.error("[ApiService.stripeClientLink] Error retrieving data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async createSubscription(jwt: string, formData: any, clientId?: string) {
    try {
        const resolvedClientId = getActiveClientIdForApi(clientId || formData?.ClientId || formData?.clientId);
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessSubscription`;
        
        // Convertir FormData a objeto JSON si es necesario
        let payload = formData;
        if (formData instanceof FormData) {
          const data: any = {};
          formData.forEach((value, key) => {
            data[key] = value;
          });
          payload = data;
        }
        
        const response = await axios.post(apiUrl, payload, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
      });
  
        return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
    } catch (error) {
        console.error("[ApiService.createSubscription] Error retrieving data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async readSubscription(jwt: string, clientId?: string) {
    try {
        const resolvedClientId = getActiveClientIdForApi(clientId);
        const apiUrl = ApiService.appendClientIdQueryParam(
          `${ApiService.apiGatewayBaseUrl}accessSubscription`,
          resolvedClientId
        );
        const config: RetriableRequestConfig = {
          _skipAuthRedirect: true,
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
          timeout: 30000,
        } as RetriableRequestConfig;
        const response = await axios.get(apiUrl, config);
  
        return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
    } catch (error) {
        console.error("[ApiService.readSubscription] Error retrieving data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async updateSubscription(jwt: string, entryId: string, data: any, clientId?: string) {
    try {
        const resolvedClientId = getActiveClientIdForApi(clientId || data?.ClientId || data?.clientId);
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessSubscription`;
        const response = await axios.patch(apiUrl, {
          EntryId: entryId,
          Data: data
        }, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
      });
  
        return response.data;
    } catch (error) {
        console.error("[ApiService.updateSubscription] Error updating subscription:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async deleteSubscription(jwt: string, entryId: string, cancelStripeSubscription: boolean = true, clientId?: string) {
    try {
        const resolvedClientId = getActiveClientIdForApi(clientId);
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessSubscription`;
        const response = await axios.delete(apiUrl, {
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
          data: {
            EntryId: entryId,
            cancelStripeSubscription: cancelStripeSubscription
          }
      });
  
        return response.data;
    } catch (error) {
        console.error("[ApiService.deleteSubscription] Error deleting subscription:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async cancelUserSubscriptionAtPeriodEnd(
    jwt: string,
    entryId: string,
    options?: {
      syncBilling?: boolean;
      preserveCatalogSubscription?: boolean;
      deleteAssociatedVps?: boolean;
      cancelAtPeriodEnd?: boolean;
    }
  ) {
    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessSubscription`;
      const response = await axios.delete(apiUrl, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        },
        data: {
          EntryId: entryId,
          cancelStripeSubscription: true,
          syncBilling: options?.syncBilling ?? true,
          preserveCatalogSubscription: options?.preserveCatalogSubscription ?? true,
          deleteAssociatedVps: options?.deleteAssociatedVps ?? false,
          cancelAtPeriodEnd: options?.cancelAtPeriodEnd ?? true,
        }
      });

      return response.data;
    } catch (error) {
      console.error("[ApiService.cancelUserSubscriptionAtPeriodEnd] Error canceling user subscription at period end:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async createUserSubscription(userId: string, signUp: SignUpForm, paymentMethodId: string) {
    try {
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessSubscription`;
        const signUpSteps = Array.isArray(signUp?.Steps) ? signUp.Steps : [];
        const readStepValue = (fieldId: string) => (
          signUpSteps.flatMap((step: any) => Array.isArray(step?.Fields) ? step.Fields : []).find((field: any) => field?.EntryId === fieldId)?.Value
        );
        const response = await axios.post(apiUrl, {
          UserId: userId,
          SubscriptionId: signUp?.Subscription?.EntryId,
          paymentMethodId: paymentMethodId,
          signUpFormId: signUp?.EntryId,
          promotionCode: signUp?.Subscription?.PromotionCode,
          email: readStepValue('d80a8906-1666-4dee-a135-18b22328bd6e'),
          name: readStepValue('52c451f5-7787-4900-94bc-e34d57696ea1'),
        },{
          headers: {
              'Content-Type': 'application/json'
          },
      });
  
        return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
    } catch (error) {
        console.error("[ApiService.createUserSubscription] Error retrieving data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async readUserData(jwt: string | undefined, clientId?: string) {
    try {
        const { sub } = jwtDecode<{ sub: string }>(jwt);
        const resolvedClientId = getActiveClientIdForApi(clientId);
        const apiUrl = resolvedClientId
          ? `${ApiService.apiGatewayBaseUrl}accessUser?UserId=${sub}&clientId=${encodeURIComponent(resolvedClientId)}`
          : `${ApiService.apiGatewayBaseUrl}accessUser?UserId=${sub}`;
        console.log("[ApiService.readUserData] Diagnostic event")
        const cacheKey = `${ApiService.getShortJwtKey(jwt)}::${apiUrl}`;
        const response = await ApiService.getCachedRequest(
          ApiService.userDataRequestCache,
          cacheKey,
          () => axios.get(apiUrl, {
            headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
          })
        );
  
      return response.data; // Retorna los datos obtenidos si es necesario
    } catch (error) {
      console.error("[ApiService.readUserData] Error sending data:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async updateUserData(jwt: string | undefined, userId: string, data: { [key: string]: any }) {
    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessUser`;
      const response = await axios.patch(apiUrl, {
        UserId: userId,
        Data: data
      }, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        },
      });
      ApiService.userDataRequestCache.clear();
      return response.data;
    } catch (error) {
      console.error("[ApiService.updateUserData] Error updating user data:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async listPushDevices(jwt: string | undefined, clientId?: string) {
    try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPushNotifications`;
      const response = await axios.post(
        apiUrl,
        { action: 'listDevices', companyId: resolvedClientId },
        { headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId) }
      );
      return response.data;
    } catch (error) {
      console.error("[ApiService.listPushDevices] Error listing push devices:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async removePushDevice(
    jwt: string | undefined,
    clientId?: string,
    endpointHash?: string,
    endpoint?: string
  ) {
    try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPushNotifications`;
      const response = await axios.post(
        apiUrl,
        {
          action: 'removeDevice',
          companyId: resolvedClientId,
          endpointHash,
          endpoint,
        },
        { headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId) }
      );
      return response.data;
    } catch (error) {
      console.error("[ApiService.removePushDevice] Error removing push device:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async clearPushDevices(jwt: string | undefined, clientId?: string) {
    try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPushNotifications`;
      const response = await axios.post(
        apiUrl,
        { action: 'clearDevices', companyId: resolvedClientId },
        { headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId) }
      );
      return response.data;
    } catch (error) {
      console.error("[ApiService.clearPushDevices] Error clearing push devices:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async sendStoryCommentPush(
    jwt: string | undefined,
    commentMessageId: string,
    clientId?: string
  ) {
    try {
      const resolvedClientId = getActiveClientIdForApi(clientId);
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPushNotifications`;
      const response = await axios.post(
        apiUrl,
        {
          action: 'sendStoryCommentPush',
          companyId: resolvedClientId,
          commentMessageId,
        },
        { headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId) }
      );
      return response.data;
    } catch (error) {
      console.error("[ApiService.sendStoryCommentPush] Error sending story comment push:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async listOwnNotifications(jwt: string | undefined, clientId?: string, limit: number = 8) {
    const resolvedClientId = getActiveClientIdForApi(clientId);
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPushNotifications`;
    const response = await axios.post(
      apiUrl,
      {
        action: 'listOwnNotifications',
        companyId: resolvedClientId,
        limit,
      },
      { headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId) }
    );
    return response.data;
  }

  static async getStripeInvoices(jwt: string | undefined, customerId?: string) {
    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessStripeClient?action=getInvoices${customerId ? `&customerId=${customerId}` : ''}`;
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        },
      });
      return response.data;
    } catch (error) {
      console.error("[ApiService.getStripeInvoices] Error fetching Stripe invoices:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static clearPublicBrandingCache(identifier?: string) {
    if (!identifier) {
      ApiService.publicBrandingRequestCache.clear();
      return;
    }
    const normalizedIdentifier = normalizeCustomAppHostname(identifier) || String(identifier || '').trim();
    const isHostLookup = normalizedIdentifier.includes('.');
    const query = isHostLookup
      ? `host=${encodeURIComponent(normalizedIdentifier)}`
      : `urlSubdomain=${encodeURIComponent(normalizedIdentifier)}`;
    ApiService.publicBrandingRequestCache.delete(`${ApiService.apiGatewayBaseUrl}accessFav?${query}&isBranding=true&tandc=false`);
    ApiService.publicBrandingRequestCache.delete(`${ApiService.apiGatewayBaseUrl}accessFav?${query}&isBranding=false&tandc=false`);
  }

  static async readBranding(identifier: string, isBranding: boolean, istandc: boolean, options?: { forceRefresh?: boolean }) {
    try {
        const normalizedIdentifier = normalizeCustomAppHostname(identifier) || String(identifier || '').trim();
        const isHostLookup = normalizedIdentifier.includes('.');
        const query = isHostLookup
          ? `host=${encodeURIComponent(normalizedIdentifier)}`
          : `urlSubdomain=${encodeURIComponent(normalizedIdentifier)}`;
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessFav?${query}&isBranding=${isBranding}&tandc=${istandc}`;
        const forceRefresh = Boolean(options?.forceRefresh);
        const cacheKey = apiUrl;
        const cached = ApiService.publicBrandingRequestCache.get(cacheKey);
        if (cached && !forceRefresh) {
          return cached;
        }
        const request = axios.get(apiUrl).then((response) => response.data)
          .catch((error) => {
            ApiService.publicBrandingRequestCache.delete(cacheKey);
            throw error;
          });
        ApiService.publicBrandingRequestCache.set(cacheKey, request);
        return request; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
    } catch (error) {
        console.error("[ApiService.readBranding] Error retrieving data:", safeErrorDiagnostic(error));
        throw error;
    }
  }
  
  static async readLandingPage(identifier: string) {
    try {
      const query = ApiService.buildPublicClientQuery(identifier);
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessLandingPage?${query}`;
      const cacheKey = apiUrl;
      const cached = ApiService.publicLandingRequestCache.get(cacheKey);
      if (cached) {
        return cached;
      }
      const request = axios.get(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
        },
      }).then((response) => response.data as LandingPageConfig)
        .catch((error) => {
          ApiService.publicLandingRequestCache.delete(cacheKey);
          throw error;
        });
      ApiService.publicLandingRequestCache.set(cacheKey, request);
      return request;
    } catch (error) {
      console.error("[ApiService.readLandingPage] Error retrieving landing page:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  /** Sign-in owns this request: abort/retry must not reuse or cancel a shared cached lookup. */
  static async readPublicClientIdentityForSignIn(identifier: string, options: { signal: AbortSignal }) {
    const query = ApiService.buildPublicClientQuery(identifier);
    const response = await axios.get(`${ApiService.apiGatewayBaseUrl}accessClient?${query}`, {
      headers: { 'Content-Type': 'application/json' },
      signal: options.signal,
      timeout: SIGN_IN_BOOTSTRAP_TIMEOUT_MS,
    });
    const client = response.data?.Client;
    resolvePublicSignInSso(client);
    return client as PublicClientIdentity;
  }

  static async readPublicClientIdentity(identifier: string) {
    try {
      const query = ApiService.buildPublicClientQuery(identifier);
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessClient?${query}`;
      const cacheKey = apiUrl;
      const cached = ApiService.publicClientRequestCache.get(cacheKey);
      if (cached) {
        return cached;
      }
      const request = axios.get(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
        },
      }).then((response) => (response.data?.Client || {}) as PublicClientIdentity)
        .catch((error) => {
          ApiService.publicClientRequestCache.delete(cacheKey);
          throw error;
        });
      ApiService.publicClientRequestCache.set(cacheKey, request);
      return request;
    } catch (error) {
      console.error("[ApiService.readPublicClientIdentity] Error retrieving public client identity:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async readPublicClientIdentityById(clientId: string) {
    try {
      const normalizedClientId = String(clientId || '').trim();
      if (!normalizedClientId) {
        throw new Error('ClientId is required');
      }
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessClient?clientId=${encodeURIComponent(normalizedClientId)}`;
      const cacheKey = apiUrl;
      const cached = ApiService.publicClientRequestCache.get(cacheKey);
      if (cached) {
        return cached;
      }
      const request = axios.get(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
        },
      }).then((response) => (response.data?.Client || {}) as PublicClientIdentity)
        .catch((error) => {
          ApiService.publicClientRequestCache.delete(cacheKey);
          throw error;
        });
      ApiService.publicClientRequestCache.set(cacheKey, request);
      return request;
    } catch (error) {
      console.error("[ApiService.readPublicClientIdentityById] Error retrieving public client identity by id:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static buildCompanySsoLoginUrl(identifier: string, sso: CompanySsoPublicConfig) {
    const hostedUiDomain = (process.env.REACT_APP_COGNITO_HOSTED_UI_DOMAIN || 'auth.orgcloud.io').trim();
    const appClientId = (process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID || '1tpv9j9juf9ie4e0clv2hrjt2o').trim();
    const providerName = sso.Cognito?.ProviderName || sso.ProviderName;
    if (!hostedUiDomain || !appClientId || !providerName) {
      return '';
    }
    const redirectUri = `${window.location.origin}/auth/sign-in/`;
    const scope = 'openid email profile';
    const tenantHint = normalizeCustomAppHostname(identifier) || String(identifier || '').trim().toLowerCase();
    return `https://${hostedUiDomain}/oauth2/authorize?identity_provider=${encodeURIComponent(providerName)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&client_id=${encodeURIComponent(appClientId)}&scope=${encodeURIComponent(scope)}&state=${encodeURIComponent(tenantHint)}`;
  }

  static async bidiVerify(user: string, data: Record<string, any>) {
    try {
        const apiUrl = `${ApiService.apiGatewayBaseUrl}bidiverify`;
        const response = await axios.post(apiUrl, data,{
            headers: {
                'Authorization': `Bearer ${user}`,
                'Content-Type': 'application/json'
            },
            });
        return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
    } catch (error) {
        console.error("[ApiService.bidiVerify] Error retrieving data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async createAutomation(
    jwt: string,
    formData: Omit<AutomationType, 'EntryId'> & { EntryId?: string; payload?: any; clientId?: string; ClientId?: string },
    clientId?: string
  ) {
    try {
        const resolvedClientId = getActiveClientIdForApi(clientId || formData.ClientId || formData.clientId);
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessAutomations`;
        const response = await axios.post(apiUrl, {
          ...formData,
          ClientId: resolvedClientId,
          clientId: resolvedClientId,
        },{
          headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
      });
  
        return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
    } catch (error) {
        console.error("[ApiService.createAutomation] Error retrieving data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async readAutomation(jwt: string, clientId?: string, automationId?: string) {
    try {
        const resolvedClientId = getActiveClientIdForApi(clientId);
        const apiUrl = ApiService.appendClientIdQueryParam(
          `${ApiService.apiGatewayBaseUrl}accessAutomations${automationId ? `/${automationId}` : ''}`,
          resolvedClientId
        );
        const config: RetriableRequestConfig = {
            _skipAuthRedirect: true,
            headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
        } as RetriableRequestConfig;
        const response = await axios.get(apiUrl, config);
  
        return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
    } catch (error) {
        console.error("[ApiService.readAutomation] Error retrieving data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async updateAutomation(
    jwt: string,
    automationId: string,
    formData: AutomationType & { clientId?: string; ClientId?: string },
    clientId?: string
  ) {
    try {
        const resolvedClientId = getActiveClientIdForApi(clientId || formData.ClientId || formData.clientId);
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessAutomations`;
        const response = await axios.put(apiUrl, {
            ...formData,
            EntryId: automationId,
            ClientId: resolvedClientId,
            clientId: resolvedClientId,
        }, {
            headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
        });
  
        return response.data;
    } catch (error) {
        console.error("[ApiService.updateAutomation] Error updating automation:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async deleteAutomation(jwt: string, automationId: string, clientId?: string) {
    try {
        const resolvedClientId = getActiveClientIdForApi(clientId);
        const apiUrl = ApiService.appendClientIdQueryParam(
          `${ApiService.apiGatewayBaseUrl}accessAutomations/${automationId}`,
          resolvedClientId
        );
        const response = await axios.delete(apiUrl, {
            headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
        });
  
        return response.data;
    } catch (error) {
        console.error("[ApiService.deleteAutomation] Error deleting automation:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async readEventBootstrap(clientId: string, jwt?: string) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents?action=bootstrapClient&ClientId=${encodeURIComponent(clientId)}`;
    const response = await axios.get(apiUrl, {
      headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
    });
    return response.data;
  }

  static async readLocalMockUsers(clientId: string, options?: { onePerRole?: boolean }) {
    if (
      typeof window === 'undefined' ||
      !isLocalDevelopmentHost(window.location.hostname)
    ) {
      throw new Error('Local mock authentication is disabled outside local development.');
    }
    const onePerRole = options?.onePerRole ? '&onePerRole=1' : '';
    const apiUrl = `${ApiService.getLocalAuthBaseUrl()}accessLocalAuth?action=users&ClientId=${encodeURIComponent(clientId)}${onePerRole}`;
    const response = await axios.get(apiUrl, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  }

  static async localMockSignIn(clientId: string, email: string, password: string = 'local') {
    if (
      typeof window === 'undefined' ||
      !isLocalDevelopmentHost(window.location.hostname)
    ) {
      throw new Error('Local mock authentication is disabled outside local development.');
    }
    const apiUrl = `${ApiService.getLocalAuthBaseUrl()}accessLocalAuth`;
    const response = await axios.post(
      apiUrl,
      { ClientId: clientId, email, password },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  }

  static async readEventDefinitions(clientId: string, jwt?: string) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents?ClientId=${encodeURIComponent(clientId)}`;
    const response = await axios.get(apiUrl, {
      headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
    });
    return response.data;
  }

  static async readEventExecutions(clientId: string, jwt?: string) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents?action=executions&ClientId=${encodeURIComponent(clientId)}`;
    const response = await axios.get(apiUrl, {
      headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
    });
    return response.data;
  }

  static async saveEventDefinition(clientId: string, definition: Record<string, any>, jwt?: string) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      {
        ...definition,
        action: 'saveDefinition',
        ClientId: clientId,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
      }
    );
    return response.data;
  }

  static async readLogicDefinitions(clientId: string, jwt?: string) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessLogicEngine?ClientId=${encodeURIComponent(clientId)}`;
    const response = await axios.get(apiUrl, {
      headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
    });
    return response.data;
  }

  static async readLogicExecutions(clientId: string, jwt?: string) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessLogicEngine?action=executions&ClientId=${encodeURIComponent(clientId)}`;
    const response = await axios.get(apiUrl, {
      headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
    });
    return response.data;
  }

  static async saveLogicDefinition(clientId: string, definition: Record<string, any>, jwt?: string) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessLogicEngine`;
    const response = await axios.post(
      apiUrl,
      {
        ...definition,
        action: 'saveDefinition',
        ClientId: clientId,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
      }
    );
    return response.data;
  }

  static async emitEventSignal(
    clientId: string,
    signal: Record<string, any>,
    context: Record<string, any> = {},
    persist: boolean = true,
    jwt?: string
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      {
        action: persist ? 'emitSignal' : 'simulateSignal',
        ClientId: clientId,
        signal,
        context,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
      }
    );
    return response.data;
  }

  static async checkDebtBlock(
    jwt: string | undefined,
    clientId: string,
    context: Record<string, any>
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      {
        action: 'checkDebtBlock',
        ClientId: clientId,
        context,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
      }
    );
    return response.data;
  }

  static async invokeSignalAction(
    jwt: string | undefined,
    clientId: string,
    binding: Record<string, any>,
    context: Record<string, any> = {},
    persist: boolean = true
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const signal = {
      type: binding.signalType || 'page.cta',
      actionName: binding.actionName || '',
      objectId: binding.objectId || '',
    };
    const response = await axios.post(
      apiUrl,
      {
        action: persist ? 'invokeSignalAction' : 'simulateSignalAction',
        ClientId: clientId,
        definitionId: binding.definitionId,
        actionName: binding.actionName,
        signal,
        context,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
      }
    );
    return response.data;
  }

  static async createOpenMatch(
    jwt: string | undefined,
    clientId: string,
    context: Record<string, any>
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      { action: 'createOpenMatch', ClientId: clientId, context },
      { headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) }
    );
    return response.data;
  }

  static async createMatch(
    jwt: string | undefined,
    clientId: string,
    context: Record<string, any>
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      { action: 'createMatch', ClientId: clientId, context },
      { headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) }
    );
    return response.data;
  }

  static async getWalletSummary(
    jwt: string | undefined,
    clientId: string,
    context: Record<string, any>
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      { action: 'getWalletSummary', ClientId: clientId, context },
      { headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) }
    );
    return response.data;
  }

  static async joinMatch(
    jwt: string | undefined,
    clientId: string,
    context: Record<string, any>
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      { action: 'joinMatch', ClientId: clientId, context },
      { headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) }
    );
    return response.data;
  }

  static async joinOpenMatch(
    jwt: string | undefined,
    clientId: string,
    context: Record<string, any>
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      { action: 'joinOpenMatch', ClientId: clientId, context },
      { headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) }
    );
    return response.data;
  }

  static async readMatchInvite(jwt: string | undefined, clientId: string, activityId: string) {
    const response = await axios.post(`${ApiService.apiGatewayBaseUrl}accessEvents`,
      { action: 'readMatchInvite', ClientId: clientId, context: { activityId } },
      { headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) });
    return response.data;
  }

  static async acceptMatchInvite(
    jwt: string | undefined,
    clientId: string,
    context: Record<string, any>
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      { action: 'acceptMatchInvite', ClientId: clientId, context },
      { headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) }
    );
    return response.data;
  }

  static async submitMatchResult(
    jwt: string | undefined,
    clientId: string,
    context: Record<string, any>
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      { action: 'submitMatchResult', ClientId: clientId, context },
      { headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) }
    );
    return response.data;
  }

  static async matchCycleAction(
    jwt: string | undefined,
    clientId: string,
    action: 'readMatchCycle' | 'withdrawMatch' | 'cancelMatch' | 'recoverMatchOperation'
      | 'requestMatchBench' | 'offerMatchBench' | 'acceptMatchBench' | 'declineMatchBench',
    context: Record<string, any>
  ) {
    const response = await axios.post(`${ApiService.apiGatewayBaseUrl}accessEvents`,
      { action, ClientId: clientId, context },
      { headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) });
    return response.data;
  }

  static async matchPostmatchAction(
    jwt: string | undefined, clientId: string,
    action: 'readMatchResult' | 'proposeMatchResult' | 'confirmMatchResult' | 'disputeMatchResult'
      | 'resolveMatchResult' | 'retractMatchResult' | 'recordMatchAttendance' | 'readPrivateMatchFeedback' | 'submitPrivateMatchFeedback',
    context: Record<string, any>
  ) {
    const response = await axios.post(`${ApiService.apiGatewayBaseUrl}accessEvents`,
      { action, ClientId: clientId, context },
      { timeout: 30000, headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) });
    return response.data;
  }

  static async runLogicFunction(
    clientId: string,
    functionName: string,
    payload: Record<string, any>,
    persist: boolean = true,
    jwt?: string
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessLogicEngine`;
    const response = await axios.post(
      apiUrl,
      {
        action: persist ? 'runFunction' : 'simulateFunction',
        ClientId: clientId,
        functionName,
        payload,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId),
      }
    );
    return response.data;
  }

  static async readPublicObjectSchema(ObjectId: string, ClientId?: string, Token?: string) {
    try {
        let apiUrl = `${ApiService.apiGatewayBaseUrl}accessPublicForm?ObjectId=${ObjectId}`;
        // Solo incluir ClientId si está presente
        if (ClientId) {
            apiUrl += `&ClientId=${ClientId}`;
        }
        // Solo incluir Token si está presente (para compatibilidad con QRs antiguos)
        if (Token) {
            apiUrl += `&Token=${Token}`;
        }
        const response = await axios.get(apiUrl, {
            headers: {
                'Content-Type': 'application/json'
            },
        });
  
        return response.data; // Retorna los datos de la respuesta para que puedan ser utilizados fuera de la función
    } catch (error) {
        console.error('[ApiService] public schema request failed');
        throw error;
    }
  }

  static async createPublicRow(formData: any) {
    try {
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPublicForm`;
        const response = await axios.post(apiUrl, formData,{
          headers: {
                'Content-Type': 'application/json'
          },
      });

        return response.data;
    } catch (error) {
        console.error("[ApiService.createPublicRow] Error retrieving data:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  /**
   * Obtener configuración de reservas del cliente (público)
   */
  static async preparePublicPayment(formData: any) {
    try {
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPublicForm`;
        const response = await axios.post(apiUrl, {
          ...formData,
          action: 'prepare_payment',
        },{
          headers: {
                'Content-Type': 'application/json'
          },
      });

        return response.data;
    } catch (error) {
        console.error("[ApiService.preparePublicPayment] Error preparing public payment:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async finalizePublicPaymentSubmission(data: {
    pendingSubmissionId: string;
    paymentIntentId: string;
  }) {
    try {
        const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPublicForm`;
        const response = await axios.post(apiUrl, {
          action: 'finalize_payment',
          ...data,
        },{
          headers: {
                'Content-Type': 'application/json'
          },
      });

        return response.data;
    } catch (error) {
        console.error("[ApiService.finalizePublicPaymentSubmission] Error finalizing public payment submission:", safeErrorDiagnostic(error));
        throw error;
    }
  }

  static async readPublicClientReservations(clientId: string) {
    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessClient?clientId=${clientId}`;
      const response = await axios.get(apiUrl, {
        headers: {
          'Content-Type': 'application/json'
        },
      });
      
      // Retornar solo la configuración de reservas
      return response.data?.Client?.Reservations || null;
    } catch (error) {
      console.error('[ApiService] public reservation configuration request failed');
      throw error;
    }
  }

  /**
   * Obtener mapa y verificar si un elemento es reservable (público)
   */
  static async readFloorPlans(jwt: string | undefined, clientId?: string) {
    const resolvedClientId = getActiveClientIdForApi(clientId);
    const apiUrl = ApiService.appendClientIdQueryParam(
      `${ApiService.apiGatewayBaseUrl}accessFloorPlans`,
      resolvedClientId
    );
    const response = await axios.get(apiUrl, {
      headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
    });
    return response.data;
  }

  static async createFloorPlan(jwt: string | undefined, floorPlan: any, clientId?: string) {
    const resolvedClientId = getActiveClientIdForApi(clientId || floorPlan?.ClientId);
    const synchronizedFloorPlan = Array.isArray(floorPlan?.Rectangles)
      ? { ...floorPlan, Areas: floorPlan.Rectangles }
      : floorPlan;
    const response = await axios.post(
      `${ApiService.apiGatewayBaseUrl}accessFloorPlans`,
      { ...synchronizedFloorPlan, ClientId: resolvedClientId, clientId: resolvedClientId },
      { headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId) }
    );
    return response.data;
  }

  static async updateFloorPlan(jwt: string | undefined, floorPlan: any, clientId?: string) {
    const resolvedClientId = getActiveClientIdForApi(clientId || floorPlan?.ClientId);
    const synchronizedFloorPlan = Array.isArray(floorPlan?.Rectangles)
      ? { ...floorPlan, Areas: floorPlan.Rectangles }
      : floorPlan;
    const response = await axios.put(
      `${ApiService.apiGatewayBaseUrl}accessFloorPlans`,
      { ...synchronizedFloorPlan, ClientId: resolvedClientId, clientId: resolvedClientId },
      { headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId) }
    );
    return response.data;
  }

  static async deleteFloorPlan(jwt: string | undefined, floorPlanId: string, clientId?: string) {
    const resolvedClientId = getActiveClientIdForApi(clientId);
    const apiUrl = ApiService.appendClientIdQueryParam(
      `${ApiService.apiGatewayBaseUrl}accessFloorPlans?EntryId=${encodeURIComponent(floorPlanId)}`,
      resolvedClientId
    );
    const response = await axios.delete(apiUrl, {
      headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
    });
    return response.data;
  }

  static async readPublicFloorPlans(clientId: string, floorPlanId?: string) {
    const params = new URLSearchParams({ clientId, public: 'true' });
    if (floorPlanId) params.set('floorPlanId', floorPlanId);
    const response = await axios.get(`${ApiService.apiGatewayBaseUrl}accessFloorPlans?${params.toString()}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (typeof response.data === 'string') {
      try {
        return JSON.parse(response.data);
      } catch (error) {
        console.warn('[ApiService] public floor-plan response could not be parsed');
      }
    }
    return response.data;
  }

  static async getReservationAvailability(params: {
    clientId: string;
    resourceRecordId?: string;
    recordId?: string;
    date?: string;
    requestedDate?: string;
    reservationObjectId?: string;
    resourceFieldId?: string;
    dateFieldId?: string;
    startTimeFieldId?: string;
    endTimeFieldId?: string;
    requestedStartTime?: string;
    requestedEndTime?: string;
    maxOccupancyTime?: number;
    floorPlanId?: string;
    areaId?: string;
    mapElementId?: string;
  }) {
    const resourceRecordId = params.resourceRecordId || params.recordId || '';
    const date = params.date || params.requestedDate || '';
    const searchParams = new URLSearchParams({
      clientId: params.clientId,
      resourceRecordId,
      date,
    });
    if (params.reservationObjectId) searchParams.set('reservationObjectId', params.reservationObjectId);
    if (params.requestedStartTime) searchParams.set('requestedStartTime', params.requestedStartTime);
    if (params.requestedEndTime) searchParams.set('requestedEndTime', params.requestedEndTime);
    if (params.maxOccupancyTime) searchParams.set('maxOccupancyTime', String(params.maxOccupancyTime));
    if (params.floorPlanId) searchParams.set('floorPlanId', params.floorPlanId);
    if (params.areaId) searchParams.set('areaId', params.areaId);
    if (params.mapElementId) searchParams.set('mapElementId', params.mapElementId);
    const response = await axios.get(`${ApiService.apiGatewayBaseUrl}public/reservationAvailability?${searchParams.toString()}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  }

  static async createReservationHold(payload: {
    clientId: string;
    attemptId: string;
    confirmationToken: string;
    reservationObjectId?: string;
    resourceRecordId: string;
    requestedDate: string;
    requestedStartTime?: string;
    requestedEndTime?: string;
    maxOccupancyTime?: number;
    floorPlanId?: string;
    areaId?: string;
    mapElementId?: string;
    formData?: Record<string, any>;
  }) {
    const response = await axios.post(`${ApiService.apiGatewayBaseUrl}public/reservationHold`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  }

  static async confirmReservationHold(payload: {
    clientId: string;
    holdId?: string;
    attemptId?: string;
    confirmationToken: string;
  }) {
    const response = await axios.post(`${ApiService.apiGatewayBaseUrl}public/reservationConfirm`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  }

  static async reservationAction(jwt: string | undefined, payload: {
    clientId: string;
    reservationId: string;
    status: 'held' | 'pending_payment' | 'confirmed' | 'cancelled' | 'expired' | 'completed';
  }) {
    const resolvedClientId = getActiveClientIdForApi(payload.clientId);
    const response = await axios.post(`${ApiService.apiGatewayBaseUrl}accessReservations/action`, payload, {
      headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
    });
    return response.data;
  }

  static async offPeakAction(jwt: string | undefined, payload: Record<string, any>) {
    const resolvedClientId = getActiveClientIdForApi(String(payload.clientId || ''));
    const response = await axios.post(`${ApiService.apiGatewayBaseUrl}accessReservations/action`, {
      ...payload,
      clientId: resolvedClientId,
    }, {
      headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), resolvedClientId),
    });
    return response.data;
  }

  static listOffPeakOpportunities(jwt: string | undefined, clientId: string) {
    return ApiService.offPeakAction(jwt, { action: 'off_peak_list', clientId });
  }

  static listOffPeakMarketplace(jwt: string | undefined, nexoClientId: string) {
    return ApiService.offPeakAction(jwt, { action: 'off_peak_marketplace', clientId: nexoClientId });
  }

  static getInstructorContext(jwt: string | undefined, clientId: string) {
    return ApiService.offPeakAction(jwt, { action: 'instructor_context', clientId });
  }

  static prepareInstructorClass(jwt: string | undefined, clientId: string, payload: Record<string, any>) {
    return ApiService.offPeakAction(jwt, { ...payload, action: 'instructor_class_prepare', clientId });
  }

  static manageInstructorBooking(jwt: string | undefined, clientId: string, reservationId: string, operation: 'cancel' | 'mark_paid' | 'mark_refunded', reference = '') {
    return ApiService.offPeakAction(jwt, { action: 'instructor_booking_manage', clientId, reservationId, operation, reference });
  }

  static createInstructorOffer(jwt: string | undefined, clientId: string, payload: Record<string, any>) {
    return ApiService.offPeakAction(jwt, { ...payload, action: 'off_peak_create_offer', clientId });
  }

  static requestInstructorAffiliation(jwt: string | undefined, clientId: string, clubClientId: string, venueId: string, mode: 'club' | 'independent' | 'both' = 'independent') {
    return ApiService.offPeakAction(jwt, {
      action: 'instructor_affiliation_request', clientId, clubClientId, venueId, mode,
    });
  }

  static async createInstructorClass(jwt: string | undefined, clientId: string, payload: Record<string, any>) {
    try {
      return await ApiService.offPeakAction(jwt, { ...payload, action: 'instructor_class_create', clientId });
    } catch (error: any) {
      // Both instructor dashboards display error.message. Preserve the server's
      // explicit closed-attempt reason instead of a generic Axios status message.
      const message = error?.response?.data?.error;
      if (typeof message === 'string' && message.trim()) throw new Error(message);
      throw error;
    }
  }

  static analyzeOffPeakCapacity(jwt: string | undefined, clientId: string) {
    return ApiService.offPeakAction(jwt, { action: 'off_peak_analyze', clientId });
  }

  static saveOffPeakPolicy(jwt: string | undefined, clientId: string, policy: Record<string, any>) {
    return ApiService.offPeakAction(jwt, { action: 'off_peak_save_policy', clientId, policy });
  }

  static updateOffPeakOpportunity(jwt: string | undefined, clientId: string, opportunityId: string, operation: 'approve' | 'publish' | 'pause' | 'reject', approvedPrice?: number, conditions?: Record<string, any>) {
    return ApiService.offPeakAction(jwt, { ...conditions, action: 'off_peak_update', clientId, opportunityId, operation, approvedPrice });
  }

  static decideOffPeakInstructorAccess(jwt: string | undefined, clientId: string, affiliationId: string, status: 'approved' | 'suspended' | 'ended', mode?: string) {
    return ApiService.offPeakAction(jwt, { action: 'off_peak_access_decision', clientId, affiliationId, status, mode });
  }

  static requestOffPeakInstructorAccess(jwt: string | undefined, clientId: string, venueId: string) {
    return ApiService.offPeakAction(jwt, { action: 'off_peak_request_access', clientId, venueId });
  }

  static createOffPeakClassHold(jwt: string | undefined, payload: Record<string, any>) {
    return ApiService.offPeakAction(jwt, { ...payload, action: 'off_peak_hold' });
  }

  static confirmOffPeakClass(jwt: string | undefined, payload: Record<string, any>) {
    return ApiService.offPeakAction(jwt, { ...payload, action: 'off_peak_confirm_class' });
  }

  private static async competitionConfigurationAction(
    action: 'resolveCompetitionConfiguration' | 'previewCompetitionEligibility' | 'validateCompetitionScore',
    clientId: string,
    context: Record<string, any>,
    jwt?: string
  ) {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessEvents`;
    const response = await axios.post(
      apiUrl,
      { action, ClientId: clientId, context },
      { headers: ApiService.getTenantScopedHeaders(jwt || TokenManager.getIdToken(), clientId) }
    );
    return response.data;
  }

  static resolveCompetitionConfiguration(clientId: string, context: Record<string, any>, jwt?: string) {
    return ApiService.competitionConfigurationAction('resolveCompetitionConfiguration', clientId, context, jwt);
  }

  static previewCompetitionEligibility(clientId: string, policy: Record<string, any>, participant: Record<string, any>, jwt?: string) {
    return ApiService.competitionConfigurationAction('previewCompetitionEligibility', clientId, { policy, participant }, jwt);
  }

  static validateCompetitionScore(clientId: string, rules: Record<string, any>, score: Record<string, any>, jwt?: string) {
    return ApiService.competitionConfigurationAction('validateCompetitionScore', clientId, { rules, score }, jwt);
  }

  static async quoteReservationPrice(jwt: string | undefined, payload: {
    clientId: string;
    resourceRecordId: string;
    requestedDate: string;
    requestedStartTime?: string;
    requestedEndTime?: string;
    durationMinutes?: number;
    participants?: number;
    competitors?: number;
    teams?: number;
  }) {
    const resolvedClientId = getActiveClientIdForApi(payload.clientId);
    const response = await axios.post(`${ApiService.apiGatewayBaseUrl}accessReservations/action`, {
      ...payload,
      clientId: resolvedClientId,
      action: 'quote',
    }, {
      headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
    });
    return response.data;
  }

  static async readReservationAgenda(jwt: string | undefined, payload: {
    clientId: string;
    date?: string;
    dateFrom?: string;
    dateTo?: string;
    resourceRecordId?: string;
    durationMinutes?: number;
  }) {
    const resolvedClientId = getActiveClientIdForApi(payload.clientId);
    const searchParams = new URLSearchParams({
      action: 'agenda',
      clientId: resolvedClientId,
    });
    if (payload.date) searchParams.set('date', payload.date);
    if (payload.dateFrom) searchParams.set('dateFrom', payload.dateFrom);
    if (payload.dateTo) searchParams.set('dateTo', payload.dateTo);
    if (payload.resourceRecordId) searchParams.set('resourceRecordId', payload.resourceRecordId);
    if (payload.durationMinutes) searchParams.set('durationMinutes', String(payload.durationMinutes));
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessReservations/action?${searchParams.toString()}`;
    const cacheKey = `${ApiService.getShortJwtKey(jwt)}::${resolvedClientId || 'default'}::${apiUrl}`;
    const response = await ApiService.getCachedRequest(
      ApiService.reservationsReadRequestCache,
      cacheKey,
      () => axios.get(apiUrl, {
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
      }),
      3000
    );
    return response.data;
  }

  static async createReservation(jwt: string | undefined, payload: {
    clientId: string;
    resourceRecordId: string;
    requestedDate: string;
    requestedStartTime?: string;
    requestedEndTime?: string;
    durationMinutes?: number;
    floorPlanId?: string;
    areaId?: string;
    mapElementId?: string;
    venueRecordId?: string;
    // Holds, payment and terminal transitions use their dedicated workflows.
    status?: 'confirmed';
    idempotencyKey?: string;
    expectedQuote?: { total: number; currency: string };
    formData?: Record<string, any>;
  }) {
    const resolvedClientId = getActiveClientIdForApi(payload.clientId);
    const response = await axios.post(`${ApiService.apiGatewayBaseUrl}accessReservations/action`, {
      ...payload,
      clientId: resolvedClientId,
      operation: 'create',
    }, {
      headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
    });
    return response.data;
  }

  static async createOpenSessionReservation(jwt: string | undefined, payload: {
    idempotencyKey?: string;
    expectedQuote?: { total: number; currency: string };
    clientId: string;
    resourceRecordId: string;
    requestedDate: string;
    requestedStartTime?: string;
    requestedEndTime?: string;
    durationMinutes?: number;
    floorPlanId?: string;
    areaId?: string;
    mapElementId?: string;
    venueRecordId?: string;
    sessionName?: string;
    sessionStatus?: string;
    levelMin?: number;
    levelMax?: number;
    capacity?: number;
    maxParticipants?: number;
    visibility?: string;
    participants?: string[];
    formData?: Record<string, any>;
    sessionData?: Record<string, any>;
  }) {
    const resolvedClientId = getActiveClientIdForApi(payload.clientId);
    const response = await axios.post(`${ApiService.apiGatewayBaseUrl}accessReservations/action`, {
      ...payload,
      clientId: resolvedClientId,
      operation: 'open_session',
    }, {
      headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
    });
    return response.data;
  }

  static async readPublicMapElement(clientId: string, mapElementId: string): Promise<any> {
    try {
      // Necesitamos obtener el mapa que contiene este elemento
      // Por ahora, asumimos que el mapa está en el objeto Page o en un objeto específico
      // Esto requerirá un endpoint específico o modificar el acceso público
      // Por ahora, retornamos null y lo manejaremos en el frontend
      return null;
    } catch (error) {
      console.error("[ApiService.readPublicMapElement] Error retrieving map element:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  /**
   * Verificar disponibilidad de un recurso para una reserva
   */
  static async checkReservationAvailability(data: {
    clientId: string;
    reservationObjectId: string;
    resourceFieldId: string;
    recordId: string;
    dateFieldId: string;
    startTimeFieldId?: string;
    endTimeFieldId?: string;
    requestedDate: string;
    requestedStartTime?: string;
    requestedEndTime?: string;
    maxOccupancyTime?: number; // en minutos
    mapElementId?: string; // ID del elemento del mapa (opcional)
  }) {
    try {
      // Construir filtros para obtener reservas del recurso
      const filters: Array<{ fieldName: string; operator: string; value: any }> = [{
        fieldName: data.resourceFieldId,
        operator: 'equals',
        value: data.recordId
      }];
      
      // Si hay mapElementId, también filtrar por él para mayor precisión
      if (data.mapElementId) {
        filters.push({
          fieldName: 'mapElementId',
          operator: 'equals',
          value: data.mapElementId
        });
      }
      
      // Obtener todas las reservas del recurso
      const reservationsData = await ApiService.readPublicRows({
        ObjectId: data.reservationObjectId,
        ClientId: data.clientId,
        filters,
        limit: 1000
      });

      const reservations = reservationsData.items || [];
      
      // Convertir fecha y hora solicitada a objeto Date
      // Si startTimeFieldId está configurado, usar ese campo directamente (tipo Date)
      // Si no, usar solo la fecha
      let requestedDateTime: Date;
      if (data.startTimeFieldId && data.requestedStartTime) {
        // Si hay campo de hora de inicio, puede ser un string de fecha/hora completo o solo hora
        // Intentar parsear como fecha completa primero
        requestedDateTime = new Date(data.requestedStartTime);
        if (isNaN(requestedDateTime.getTime())) {
          // Si falla, asumir que es solo hora (HH:MM) y combinarlo con la fecha
          const [hours, minutes] = data.requestedStartTime.split(':').map(Number);
          requestedDateTime = new Date(data.requestedDate);
          requestedDateTime.setHours(hours, minutes, 0, 0);
        }
      } else {
        requestedDateTime = new Date(data.requestedDate);
      }
      
      // Calcular tiempo de fin (usar endTime si está disponible, sino usar maxOccupancyTime)
      let endDateTime: Date;
      if (data.endTimeFieldId && data.requestedEndTime) {
        // Si hay campo de hora de fin, puede ser un string de fecha/hora completo o solo hora
        // Intentar parsear como fecha completa primero
        endDateTime = new Date(data.requestedEndTime);
        if (isNaN(endDateTime.getTime())) {
          // Si falla, asumir que es solo hora (HH:MM) y combinarlo con la fecha de inicio
          const [hours, minutes] = data.requestedEndTime.split(':').map(Number);
          endDateTime = new Date(requestedDateTime);
          endDateTime.setHours(hours, minutes, 0, 0);
        }
      } else if (data.maxOccupancyTime) {
        endDateTime = new Date(requestedDateTime.getTime() + data.maxOccupancyTime * 60000);
      } else {
        // Por defecto, 2 horas si no se especifica
        endDateTime = new Date(requestedDateTime.getTime() + 120 * 60000);
      }

      // Verificar conflictos con reservas existentes
      for (const reservation of reservations) {
        const resDate = reservation.Data?.[data.dateFieldId];
        const resStartTime = data.startTimeFieldId ? reservation.Data?.[data.startTimeFieldId] : null;
        const resEndTime = data.endTimeFieldId ? reservation.Data?.[data.endTimeFieldId] : null;
        
        if (!resDate) continue;
        
        // Construir fecha/hora de inicio de la reserva existente
        let resDateTime: Date;
        if (resStartTime) {
          // Si hay campo de hora de inicio, puede ser un string de fecha/hora completo o solo hora
          // Intentar parsear como fecha completa primero
          resDateTime = new Date(resStartTime);
          if (isNaN(resDateTime.getTime())) {
            // Si falla, asumir que es solo hora (HH:MM) y combinarlo con la fecha
            const [hours, minutes] = resStartTime.split(':').map(Number);
            resDateTime = new Date(resDate);
            resDateTime.setHours(hours, minutes, 0, 0);
          }
        } else {
          resDateTime = new Date(resDate);
        }
        
        // Construir fecha/hora de fin de la reserva existente
        let resEndDateTime: Date;
        if (resEndTime) {
          // Si hay campo de hora de fin, puede ser un string de fecha/hora completo o solo hora
          // Intentar parsear como fecha completa primero
          resEndDateTime = new Date(resEndTime);
          if (isNaN(resEndDateTime.getTime())) {
            // Si falla, asumir que es solo hora (HH:MM) y combinarlo con la fecha de inicio
            const [hours, minutes] = resEndTime.split(':').map(Number);
            resEndDateTime = new Date(resDateTime);
            resEndDateTime.setHours(hours, minutes, 0, 0);
          }
        } else if (data.maxOccupancyTime) {
          resEndDateTime = new Date(resDateTime.getTime() + data.maxOccupancyTime * 60000);
        } else {
          resEndDateTime = new Date(resDateTime.getTime() + 120 * 60000);
        }
        
        // Verificar si hay solapamiento
        // Hay conflicto si: (requestedStart < resEnd && requestedEnd > resStart)
        if (requestedDateTime < resEndDateTime && endDateTime > resDateTime) {
          return {
            available: false,
            conflictWith: reservation.EntryId,
            conflictDate: resDate,
            conflictStartTime: resStartTime,
            conflictEndTime: resEndTime
          };
        }
      }
      
      return { available: true };
    } catch (error) {
      console.error("[ApiService.checkReservationAvailability] Error checking reservation availability:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  /**
   * Leer filas públicamente (sin autenticación)
   */
  static async readPublicRows(params: {
    ObjectId: string;
    ClientId: string;
    publicProfileId?: string;
    filters?: Array<{ fieldName: string; operator: string; value: any }>;
    limit?: number;
    suppressErrorLog?: boolean;
    jwt?: string;
  }) {
    try {
      // Las lecturas anónimas usan una ruta separada cuya Lambda solo devuelve
      // objetos/campos permitidos públicamente. Con JWT conservamos la ruta
      // autenticada para no degradar las consultas internas existentes.
      const queryData = {
        ObjectId: params.ObjectId,
        ClientId: params.ClientId,
        ...(params.publicProfileId ? { publicProfileId: params.publicProfileId } : {}),
        filters: params.filters || [],
        limit: params.limit || 1000
      };
      
      const apiUrl = `${ApiService.apiGatewayBaseUrl}${params.jwt ? 'accessQueryData' : 'accessPublicQueryData'}`;
      const response = await ApiService.withLocalGatewayRetry(() =>
        axios.post(apiUrl, queryData, {
          headers: {
            'Content-Type': 'application/json',
            ...(params.jwt ? { Authorization: `Bearer ${params.jwt}` } : {}),
          },
          _skipAuthRedirect: true,
        } as any)
      );
      
      return response.data;
    } catch (error) {
      if (!params.suppressErrorLog) {
        console.error('[ApiService] public rows request failed');
      }
      throw error;
    }
  }

  static async readPublicPurchaseReceipt(params: {
    clientId: string;
    objectId: string;
    ticketId: string;
    accessKey: string;
  }): Promise<string> {
    const searchParams = new URLSearchParams({
      action: 'receipt',
      ClientId: params.clientId || '',
      ObjectId: params.objectId || '',
      TicketId: params.ticketId || '',
      accessKey: params.accessKey || '',
    });

    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessPublicForm?${searchParams.toString()}`;
      const htmlClient = axios.create();
      const response = await htmlClient.get(apiUrl, {
        responseType: 'text',
        validateStatus: (status) => status < 500,
        headers: {
          Accept: 'text/html',
        },
      });
      if (response.status >= 400) {
        const message = response.status === 403
          ? 'Este enlace de recibo necesita una clave privada valida.'
          : 'No hemos podido localizar este recibo de compra.';
        throw new Error(message);
      }
      return typeof response.data === 'string' ? response.data : String(response.data || '');
    } catch (error) {
      console.error("[ApiService.readPublicPurchaseReceipt] Error reading public purchase receipt:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async requestBubloudSession(
    jwt: string,
    payload: BubloudSessionRequestPayload
  ): Promise<BubloudSessionResponse> {
    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessBubloud`;
      const sanitizedKeyScope =
        payload?.keyScope === 'personal' || payload?.keyScope === 'company'
          ? payload.keyScope
          : 'company';
      const response = await axios.post(apiUrl, { ...payload, keyScope: sanitizedKeyScope }, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
      } as any);
      return response.data as BubloudSessionResponse;
    } catch (error) {
      console.error("[ApiService.requestBubloudSession] Error requesting Bubloud session:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async connectBubloudSession(
    jwt: string,
    sessionId: string,
    payload: {
      vm_id: string;
      bubble_id: string;
      device_id?: string;
      scope?: string[];
      capabilities?: Record<string, any>;
      profile?: string;
      vm_endpoint?: string;
      sessionStart?: BubloudSessionStartPayload;
    }
  ): Promise<BubloudSessionConnectResponse> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessBubloud/sessions/${encodeURIComponent(
      sessionId
    )}/connect`;
    const response = await axios.post(apiUrl, payload, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
    } as any);
    return response.data as BubloudSessionConnectResponse;
  }

  static async claimBubloudTrialSession(
    jwt: string,
    payload?: {
      keyScope?: 'personal' | 'company';
      metadata?: Record<string, any>;
    }
  ): Promise<BubloudTrialSessionResponse> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessBubloud`;
    const response = await axios.post(
      apiUrl,
      {
        action: 'claim_trial_session',
        ...(payload || {}),
      },
      {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
      } as any
    );
    return response.data as BubloudTrialSessionResponse;
  }

  static async captureAnalyticsEvent(
    jwt: string,
    payload: AnalyticsCaptureRequestPayload
  ): Promise<{ accepted?: boolean; ok?: boolean; [key: string]: any }> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}analytics/capture-proxy`;
    const response = await axios.post(apiUrl, payload, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      _skipAuthRedirect: true,
    } as any);
    return response.data;
  }

  static async capturePublicAnalyticsEvent(
    payload: AnalyticsCaptureRequestPayload
  ): Promise<{ accepted?: boolean; ok?: boolean; [key: string]: any }> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}analytics/public-capture`;
    const response = await axios.post(apiUrl, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
      _skipAuthRedirect: true,
    } as any);
    return response.data;
  }

  static async reportDesktopCapabilities(
    jwt: string,
    payload: DesktopCapabilitiesPayload
  ): Promise<{ ok: boolean; [key: string]: any }> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessBubloud`;
    const response = await axios.post(
      apiUrl,
      {
        action: 'report_desktop_capabilities',
        ...payload,
      },
      {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        _skipAuthRedirect: true,
      } as any
    );
    return response.data;
  }

  /**
   * Actualización de modo/geometry (feature BUBLOUD_DEVICE_MODE_V2). Si el backend no expone la ruta, falla silenciosamente en el caller.
   */
  static async updateBubloudSessionMode(
    jwt: string,
    sessionId: string,
    payload: {
      bubbleId: string;
      vmId: string;
      capabilities: Record<string, unknown>;
      sessionStart?: BubloudSessionStartPayload;
    }
  ): Promise<{
    ok?: boolean;
    chosenMode?: string;
    targetGeometry?: { width: number; height: number };
    uiScalePercent?: number;
    reconnectRequired?: boolean;
  }> {
    // Mismo origen CORS que el resto de accessBubloud: POST raíz + action (la ruta /sessions/.../mode a veces no está desplegada en API GW y el preflight OPTIONS falla desde localhost).
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessBubloud`;
    const response = await axios.post(
      apiUrl,
      {
        action: 'update_session_mode',
        sessionId,
        bubbleId: payload.bubbleId,
        vmId: payload.vmId,
        capabilities: payload.capabilities,
        ...(payload.sessionStart ? { sessionStart: payload.sessionStart } : {}),
      },
      {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        _skipAuthRedirect: true,
      } as any
    );
    return response.data;
  }

  static async ingestBubloudTransportMetrics(
    jwt: string,
    payload: {
      sessionId?: string;
      bubbleId?: string;
      vmId?: string;
      transport: string;
      samples: unknown[];
    }
  ): Promise<{ ok?: boolean }> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessBubloud`;
    const response = await axios.post(
      apiUrl,
      {
        action: 'ingest_transport_metrics',
        ...payload,
      },
      {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        _skipAuthRedirect: true,
      } as any
    );
    return response.data;
  }

  /**
   * Check VPS order status and get info when ready
   * @param jwt - JWT token for authentication
   * @param provider - VPS provider ('ovhcloud' or 'contabo')
   * @param orderId - Order ID or VPS ID to check
   * @returns VPS status information
   */
  static async checkVPSStatus(
    jwt: string,
    provider: 'ovhcloud' | 'hetzner' | 'contabo' | 'ionos',
    orderId: string
  ): Promise<{
    order_id: string;
    order_status: string;
    vps_ready: boolean;
    vps_info?: {
      service_name: string;
      ip: string;
      state: string;
      model?: string;
    };
  }> {
    try {
      const lambdaName = provider === 'contabo' ? 'accessContabo' : 'accessOVH';
      const apiUrl = `${ApiService.apiGatewayBaseUrl}${lambdaName}`;
      const response = await axios.post(apiUrl, {
        action: 'check_order_status',
        order_id: orderId
      }, {
        headers: ApiService.getTenantScopedHeaders(jwt),
      });
      return response.data;
    } catch (error) {
      console.error("[ApiService.checkVPSStatus] Error checking VPS status:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  /**
   * Get VPS status from DynamoDB by VPS ID
   * @param jwt - JWT token for authentication  
   * @param provider - VPS provider ('ovhcloud' or 'contabo')
   * @param vpsId - VPS ID to check
   * @returns VPS status from database
   */
  static async getVPSStatusById(
    jwt: string,
    provider: 'ovhcloud' | 'hetzner' | 'contabo' | 'ionos',
    vpsId: string
  ): Promise<{
    found: boolean;
    vps_id?: string;
    service_name?: string;
    ip?: string;
    status?: string;
    provider?: string;
    ssm_instance_id?: string;
    error?: string;
  }> {
    try {
      const lambdaName = provider === 'contabo' ? 'accessContabo' : 'accessOVH';
      const apiUrl = `${ApiService.apiGatewayBaseUrl}${lambdaName}`;
      const response = await axios.post(apiUrl, {
        action: 'get_vps_status_by_id',
        vps_id: vpsId
      }, {
        headers: ApiService.getTenantScopedHeaders(jwt),
      });
      return response.data;
    } catch (error) {
      console.error("[ApiService.getVPSStatusById] Error getting VPS status:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  /**
   * Repara el stack de escritorio remoto en la VPS vía SSM (accessOVH → repair_desktop_stack).
   * Solo debe exponerse en UI de desarrollo; en producción usar pipelines o consola con cuidado.
   */
  static async repairDesktopStack(
    jwt: string,
    options: {
      vpsId: string;
      provider?: 'ovhcloud' | 'hetzner' | 'contabo' | 'ionos';
      timeoutSeconds?: number;
    }
  ): Promise<{
    status?: string;
    vps_id?: string;
    base_user?: string;
    command_id?: string;
    StandardOutputContent?: string;
    StandardErrorContent?: string;
    error?: string;
    [key: string]: any;
  }> {
    const provider = options.provider || 'ovhcloud';
    const lambdaName = provider === 'contabo' ? 'accessContabo' : 'accessOVH';
    const apiUrl = `${ApiService.apiGatewayBaseUrl}${lambdaName}`;
    const vpsId = String(options.vpsId || '').trim();
    if (!vpsId) {
      throw new Error('vpsId requerido');
    }
    const rawTimeout = options.timeoutSeconds;
    const timeoutBase =
      typeof rawTimeout === 'number' && Number.isFinite(rawTimeout) ? rawTimeout : 300;
    const timeout = Math.min(Math.max(timeoutBase, 120), 600);
    const response = await axios.post(
      apiUrl,
      {
        action: 'repair_desktop_stack',
        vps_id: vpsId,
        timeout,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async listTenantVps(
    jwt: string
  ): Promise<{ tenant_id?: string; items?: any[] }> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      { action: 'list_tenant_vps' },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async listVpsManagementOverview(jwt: string): Promise<any> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      { action: 'list_vm_management_overview' },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async listBubleSoftwareCatalog(jwt: string): Promise<any> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      { action: 'list_buble_software_catalog' },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async listTenantVpsLogs(jwt: string, vpsId: string, limit = 20): Promise<any> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      { action: 'list_tenant_vps_logs', vps_id: vpsId, limit },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async listTenantBubleJobs(jwt: string, vpsId: string, limit = 20): Promise<any> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      { action: 'list_tenant_vps_jobs', vps_id: vpsId, limit },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async rebootTenantVps(jwt: string, vpsId: string): Promise<any> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      { action: 'reboot_tenant_vps', vps_id: vpsId },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async deleteTenantVps(jwt: string, vpsId: string): Promise<any> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      { action: 'delete_tenant_vps', vps_id: vpsId },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async repairTenantVps(jwt: string, vpsId: string): Promise<any> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      { action: 'repair_tenant_vps', vps_id: vpsId },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async installTenantSoftware(
    jwt: string,
    vpsId: string,
    softwareId: string
  ): Promise<any> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      {
        action: 'install_tenant_software',
        vps_id: vpsId,
        software_id: softwareId,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async installTenantSoftwareBundle(
    jwt: string,
    vpsId: string,
    bundleId = 'default_profile'
  ): Promise<any> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      {
        action: 'install_tenant_software_bundle',
        vps_id: vpsId,
        bundle_id: bundleId,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  static async uninstallTenantSoftware(
    jwt: string,
    vpsId: string,
    softwareId: string
  ): Promise<any> {
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessOVH`;
    const response = await axios.post(
      apiUrl,
      {
        action: 'uninstall_tenant_software',
        vps_id: vpsId,
        software_id: softwareId,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt),
      }
    );
    return response.data;
  }

  /**
   * Create a Stripe Payment Intent for store checkout
   * @param paymentData - Payment data including amount, currency, items, and shipping address
   */
  static async getPaymentAvailability(clientId: string): Promise<{
    electronicPaymentsEnabled: boolean; walletPaymentsEnabled: boolean; mode: string; message: string;
  }> {
    const response = await axios.get(`${ApiService.apiGatewayBaseUrl}accessStorePayment`, { params: { clientId } });
    return response.data;
  }

  static async createPaymentIntent(paymentData: {
    clientId: string;
    amount: number;
    currency: string;
    items: Array<{ productId: string; quantity: number; price: number }>;
    shippingAddress: any;
    promotionCode?: string;
    language?: string;
  }) {
    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessStorePayment`;
      const response = await axios.post(apiUrl, paymentData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error("[ApiService.createPaymentIntent] Error creating payment intent:", safeErrorDiagnostic(error));
      throw new Error(error?.response?.data?.error || error.message);
    }
  }

  /**
   * Create a generic OrgCloud split payment against a configured payment_record object.
   */
  static async createSplitPaymentIntent(paymentData: {
    clientId: string;
    paymentObjectId: string;
    amount: number;
    currency?: string;
    source?: string;
    description?: string;
    context?: Record<string, unknown>;
    paymentRecordId?: string;
  }) {
    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessStorePayment`;
      const response = await axios.post(apiUrl, {
        action: 'create_split_payment_intent',
        currency: 'eur',
        ...paymentData,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error("[ApiService.createSplitPaymentIntent] Error creating split payment intent:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  /**
   * Confirm an order after successful payment, updating its status to "processing" (en trámite)
   * in both StoreOrders and Data tables.
   * @param confirmData - Order confirmation data
   */
  static async confirmOrder(confirmData: {
    orderId: string;
    paymentIntentId: string;
    clientId: string;
    language?: string;
  }) {
    try {
      const apiUrl = `${ApiService.apiGatewayBaseUrl}accessStorePayment`;
      const response = await axios.post(apiUrl, {
        action: 'confirm_order',
        ...confirmData,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error("[ApiService.confirmOrder] Error confirming order:", safeErrorDiagnostic(error));
      // No lanzar error — la confirmación es best-effort
      // El pedido ya fue pagado, el webhook también actualizará el estado
      return { error: error.message };
    }
  }

  /**
   * Update a data row with VPS information manually
   * @param jwt - JWT token for authentication
   * @param provider - VPS provider
   * @param entryId - Row entry ID to update
   * @param objectId - Object/schema ID
   * @param vpsInfo - VPS information to save
   * @returns Update result
   */
  static async updateRowVPSInfo(
    jwt: string,
    provider: 'ovhcloud' | 'contabo',
    entryId: string,
    objectId: string,
    vpsInfo: {
      vps_id: string;
      vps_ip?: string;
      ssm_instance_id?: string;
      status?: string;
    }
  ): Promise<{ message: string; entry_id: string; vps_id: string; vps_ip?: string }> {
    try {
      const lambdaName = provider === 'ovhcloud' ? 'accessOVH' : 'accessContabo';
      const apiUrl = `${ApiService.apiGatewayBaseUrl}${lambdaName}`;
      const response = await axios.post(apiUrl, {
        action: 'update_row_vps_info',
        entry_id: entryId,
        object_id: objectId,
        ...vpsInfo
      }, {
        headers: ApiService.getTenantScopedHeaders(jwt),
      });
      return response.data;
    } catch (error) {
      console.error("[ApiService.updateRowVPSInfo] Error updating row VPS info:", safeErrorDiagnostic(error));
      throw error;
    }
  }

  static async getAnalyticsQuery(
    jwt: string,
    params?: Record<string, string | number | boolean>,
    clientId?: string
  ) {
    const resolvedClientId = getActiveClientIdForApi(clientId);
    const baseUrl = ApiService.appendClientIdQueryParam(
      `${ApiService.apiGatewayBaseUrl}analytics/query`,
      resolvedClientId
    );
    const searchParams = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      searchParams.append(key, String(value));
    });
    const apiUrl = searchParams.toString()
      ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${searchParams.toString()}`
      : baseUrl;
    const headers = ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false });

    try {
      const response = await axios.get(apiUrl, { headers });
      return normalizeAnalyticsResponse(response.data);
    } catch (error: any) {
      const explicitQuery = params?.query != null;
      const periodDays = parseAnalyticsPeriodToDays(params?.period);
      const status = error?.response?.status;

      if (!explicitQuery && periodDays && status === 400) {
        const fallback = await ApiService.getAnalyticsEventsSummary(jwt, { days: periodDays }, resolvedClientId);
        return {
          ...fallback,
          fallbackApplied: true,
          sourceEndpoint: 'analytics/events-summary',
          fallbackReason: 'analytics/query returned 400 for period summary',
        };
      }
      throw error;
    }
  }

  static async getAnalyticsEventsSummary(
    jwt: string,
    params?: { days?: number; limit?: number; offset?: number },
    clientId?: string
  ) {
    const resolvedClientId = getActiveClientIdForApi(clientId);
    const baseUrl = ApiService.appendClientIdQueryParam(
      `${ApiService.apiGatewayBaseUrl}analytics/events-summary`,
      resolvedClientId
    );
    const searchParams = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      searchParams.append(key, String(value));
    });
    const apiUrl = searchParams.toString()
      ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${searchParams.toString()}`
      : baseUrl;

    const response = await axios.get(apiUrl, {
      headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId, { includeClientHeader: false }),
    });
    return {
      ...normalizeAnalyticsResponse(response.data),
      sourceEndpoint: 'analytics/events-summary',
    };
  }

  static async getAnalyticsGlobalQuery(
    jwt: string,
    params?: Record<string, string | number | boolean>,
  ) {
    const searchParams = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      searchParams.append(key, String(value));
    });
    const apiUrl = searchParams.toString()
      ? `${ApiService.apiGatewayBaseUrl}analytics/global/query?${searchParams.toString()}`
      : `${ApiService.apiGatewayBaseUrl}analytics/global/query`;

    const response = await axios.get(apiUrl, {
      headers: ApiService.getTenantScopedHeaders(jwt, undefined, { includeClientHeader: false }),
    });
    return normalizeAnalyticsResponse(response.data);
  }

  static async createMediaUpload(
    jwt: string,
    payload: {
      clientId?: string;
      memberId?: string;
      activityId?: string;
      fileName: string;
      contentType: string;
      contentLength: number;
      retentionMode: 'story' | 'saved' | 'highlight';
      storyTtlDays?: number;
      maxHighlights?: number;
      sourceSurface?: string;
    },
    file: File,
  ) {
    const resolvedClientId = getActiveClientIdForApi(payload.clientId);
    const apiUrl = `${ApiService.apiGatewayBaseUrl}accessMediaUpload`;
    const createResponse = await axios.post(
      apiUrl,
      {
        action: 'createUploadUrl',
        ...payload,
        clientId: resolvedClientId,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
      },
    );
    const uploadUrl = createResponse.data?.uploadUrl;
    const uploadHeaders = createResponse.data?.headers || { 'Content-Type': payload.contentType };
    const asset = createResponse.data?.asset;
    if (!uploadUrl || !asset?.EntryId) {
      throw new Error('No se pudo preparar la subida multimedia');
    }
    await axios.put(uploadUrl, file, {
      headers: uploadHeaders,
    });
    const completeResponse = await axios.post(
      apiUrl,
      {
        action: 'completeUpload',
        clientId: resolvedClientId,
        mediaId: asset.EntryId,
      },
      {
        headers: ApiService.getTenantScopedHeaders(jwt, resolvedClientId),
      },
    );
    return completeResponse.data?.asset || asset;
  }
}

export default ApiService;
